import { apiClient } from "./client";
import { compareInstantsAtMicrosecondPrecision } from "../lib/temporalInstant";

export const CANONICAL_LINEAGE_CONTRACT_VERSION = "thread.lineage.v2" as const;
export const CANONICAL_LINEAGE_PATH = "/thread/v1/provenance/lineage" as const;
export const CANONICAL_LINEAGE_PROJECTION_CONTRACT_VERSION = "thread.lineage.projection.v2" as const;
export const CANONICAL_LINEAGE_PROJECTION_PATH = "/thread/v1/provenance/lineage/query" as const;
export const CANONICAL_LINEAGE_MIN_SCHEMA_MIGRATION = "20260810210000" as const;

const DEFAULT_LINEAGE_MAX_DEPTH = 8;
const DEFAULT_LINEAGE_MAX_RECORDS = 200;
const DEFAULT_LINEAGE_MAX_PATHS = 1000;

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };
export type JsonObject = { [key: string]: JsonValue };

export type CanonicalLineageProjection =
  | "snapshot"
  | "neighbourhood"
  | "trace"
  | "search"
  | "focus"
  | "hierarchy"
  | "summary"
  | "dsm";

export type CanonicalLineageDirection = "upstream" | "downstream" | "both";

export type CanonicalLineageEntityKind =
  | "artifact"
  | "artifact_version"
  | "operation_spec"
  | "operation_run"
  | "tool"
  | "actor"
  | "binding"
  | "binding_state_event"
  | "operation_state_event"
  | "supersession"
  | "merge_input"
  | "topology_retirement"
  | "lifecycle_event"
  | "decision"
  | "decision_evidence"
  | "decision_status_event"
  | "journal_entry"
  | "unknown";

export type CanonicalLineageChangeCause =
  | "geometry_changed"
  | "mesh_changed"
  | "solver_version_changed"
  | "coordinate_frame_changed"
  | "unit_system_changed"
  | "schema_changed"
  | "reference_area_changed"
  | "parameter_changed"
  | "metadata_only_changed"
  | "comment_only_changed"
  | "unknown_impact"
  | "manual_override";

export type CanonicalDecisionStatus =
  | "proposed"
  | "ratified"
  | "under_review"
  | "pending_ratification"
  | "withdrawn"
  | "superseded";

export interface CanonicalLineageRequest {
  project_id: string;
  root_artifact_version_ids: string[];
  direction?: CanonicalLineageDirection;
  max_depth?: number;
  max_records?: number;
  max_paths?: number;
  as_of?: string | null;
  recorded_as_of?: string;
  snapshot_token?: string;
}

export type CanonicalLineageQueryProjection =
  | "snapshot"
  | "search"
  | "focus"
  | "neighbourhood"
  | "trace"
  | "hierarchy"
  | "summary";

export interface CanonicalLineageArtifactVersionRoot {
  entity_kind: "artifact_version";
  id: string;
}

export interface CanonicalLineageQueryFilters {
  artifact_kinds?: string[];
  lifecycle_states?: string[];
  model_classifications?: CanonicalModelInstanceClassification[];
  name_contains?: string;
  created_after?: string;
  created_before?: string;
}

export type CanonicalLineageQueryGroupBy = "artifact_kind" | "lifecycle_state" | "model_classification";

export interface CanonicalLineageQueryRequest {
  project_id: string;
  projection: CanonicalLineageQueryProjection;
  root_refs?: CanonicalLineageArtifactVersionRoot[];
  direction?: CanonicalLineageDirection;
  max_depth?: number;
  max_records?: number;
  max_paths?: number;
  page_size?: number;
  cursor?: string;
  as_of?: string | null;
  recorded_as_of?: string;
  snapshot_token?: string;
  query?: string;
  filters?: CanonicalLineageQueryFilters;
  group_by?: CanonicalLineageQueryGroupBy;
}

export interface CanonicalLineageProjectionBucket {
  dimension: CanonicalLineageQueryGroupBy;
  key: string;
  label: string;
  count: number;
  representative_root?: CanonicalLineageArtifactVersionRoot | null;
}

export interface CanonicalLineageProjectionResult {
  contract_version: typeof CANONICAL_LINEAGE_PROJECTION_CONTRACT_VERSION;
  projection: CanonicalLineageQueryProjection;
  effective_as_of: string;
  recorded_as_of: string;
  snapshot_token: string;
  roots: CanonicalLineageArtifactVersionRoot[];
  lineage: CanonicalLineageReadModel;
  buckets: CanonicalLineageProjectionBucket[];
  next_cursor: string | null;
  truncated: boolean;
  warnings: string[];
}

export interface CanonicalLineageProjectionEnvelope {
  schema_version: "thread.api.v1";
  request_id: string;
  data: CanonicalLineageProjectionResult;
  page: { next_cursor: string | null; truncated: boolean };
  provenance: {
    source: "thread_core";
    projection_version: typeof CANONICAL_LINEAGE_PROJECTION_CONTRACT_VERSION;
    schema_migration: string;
  };
  warnings: string[];
}

export interface CanonicalLineageRecordRef {
  entity_kind: CanonicalLineageEntityKind;
  id: string;
}

export interface CanonicalLineageBoundary extends CanonicalLineageRecordRef {
  reason: "outside_projection" | "not_found" | "unsupported";
  detail: string | null;
}

export interface CanonicalLineageContext {
  project_id: string;
  projection: CanonicalLineageProjection;
  generated_at: string;
  as_of: string;
  recorded_as_of: string;
  snapshot_token: string;
  temporal_history: {
    status: "complete" | "incomplete";
    marker: "temporal_history_complete" | "temporal_history_incomplete";
  };
  limits: {
    resource_limits_version: "thread.lineage-resource-limits.v2";
    max_depth: number;
    max_records: number;
    max_paths: number;
    max_boundaries: 10000;
    json_max_depth: 32;
    json_max_nodes: 4096;
    json_field_max_canonical_bytes: 262144;
    json_aggregate_max_nodes: 32768;
    json_aggregate_max_canonical_bytes: 2097152;
    document_max_canonical_bytes: 8388608;
  };
  root_ids: string[];
  boundary: CanonicalLineageBoundary[];
  truncated: boolean;
}

export type CanonicalModelInstanceClassification = "generic" | "instance" | "unclassified";

export type CanonicalModelInstanceRef =
  | { classification: "instance"; generic_artifact_id: string }
  | { classification: "generic" | "unclassified"; generic_artifact_id: null };

export interface CanonicalContentIdentity {
  algorithm: "sha256";
  digest: string;
  canonicalizer: string | null;
  canonicalizer_version: string | null;
  byte_length: number | null;
  media_type: string;
  source_content_hash: string | null;
}

export interface CanonicalArtifactRecord {
  entity_kind: "artifact";
  id: string;
  logical_id: string;
  project_id: string;
  kind: string;
  name: string;
  sensitivity_tier_id: string;
  sensitivity_tier_name: string;
  metadata: JsonObject;
  created_by: string;
  created_at: string;
  effective_at: string;
  recorded_at: string;
  model_instance: CanonicalModelInstanceRef;
}

export interface CanonicalSupersessionSummary {
  id: string;
  old_artifact_version_id: string;
  new_artifact_version_id: string;
  change_type: CanonicalLineageChangeCause;
  reason: string | null;
  created_by: string;
  created_at: string;
  effective_at: string;
  recorded_at: string;
}

export interface CanonicalArtifactVersionRecord {
  entity_kind: "artifact_version";
  id: string;
  artifact_id: string;
  project_id: string;
  sensitivity_tier_id: string;
  sensitivity_tier_name: string;
  content_identity: CanonicalContentIdentity;
  structured_content: JsonValue | null;
  metadata: JsonObject;
  schema_id: string | null;
  unit_context_id: string | null;
  coordinate_frame_id: string | null;
  blob_ref: string | null;
  structured_value_ref: string | null;
  lifecycle_state:
    | "active"
    | "invalidated"
    | "superseded"
    | "redacted"
    | "abandoned"
    | "inapplicable";
  considered_valid_at_time: boolean;
  authoritative_for_new_work_at_time: boolean;
  lifecycle_basis:
    | "initial_active_no_lifecycle_event"
    | "derived_from_exact_lifecycle_timeline"
    | "independent_state_selector";
  created_by: string;
  created_at: string;
  effective_at: string;
  recorded_at: string;
}

export interface CanonicalRoleContract {
  role: string;
  required: boolean;
  min_count: number;
  max_count: number;
}

export interface CanonicalOperationSpecRecord {
  entity_kind: "operation_spec";
  id: string;
  name: string;
  version: number;
  operation_type: string;
  parameter_schema: JsonObject;
  input_roles: CanonicalRoleContract[];
  output_roles: CanonicalRoleContract[];
  definition_hash: string;
  created_at: string;
  effective_at: string;
  recorded_at: string;
}

export interface CanonicalToolSnapshotRecord {
  entity_kind: "tool";
  id: string;
  name: string;
  version: string;
  build_id: string | null;
  source_ref: string | null;
  metadata: JsonObject;
  definition_hash: string;
  temporal_basis: "derived_from_operation_run";
  owning_operation_ids: string[];
}

export interface CanonicalActorRecord {
  entity_kind: "actor";
  id: string;
  actor_type: "user" | "service" | "imported" | "unknown";
  resolution: "resolved" | "id_only" | "redacted";
  name: string | null;
  role: string | null;
  metadata: JsonObject;
  temporal_basis: "current_authorization_overlay";
}

export interface CanonicalEnvironmentSnapshot {
  runtime: string;
  operating_system: string | null;
  architecture: string | null;
  container_digest: string | null;
  dependencies: Record<string, string>;
  metadata: JsonObject;
}

export interface CanonicalAssumptionSnapshot {
  name: string;
  statement: string;
  reference: string | null;
}

export interface CanonicalOperationRunRecord {
  entity_kind: "operation_run";
  id: string;
  project_id: string;
  operation_spec_id: string;
  actor_id: string;
  tool_id: string;
  idempotency_key: string;
  parameters: JsonValue;
  environment: CanonicalEnvironmentSnapshot;
  assumptions: CanonicalAssumptionSnapshot[];
  config_hash: string | null;
  source_ref: string | null;
  metadata: JsonObject;
  request_hash: string;
  audit_receipt_id: string | null;
  domain_event_id: string | null;
  started_at: string;
  completed_at: string;
  status: string;
  created_at: string;
  effective_at: string;
  recorded_at: string;
}

export type CanonicalBindingLifecycleState =
  | "unrecorded"
  | "fresh"
  | "stale"
  | "invalidated"
  | "superseded"
  | "pinned"
  | "deferred";

export interface CanonicalBindingState {
  state: CanonicalBindingLifecycleState;
  basis: "explicit_event" | "unrecorded";
  change_cause: CanonicalLineageChangeCause | null;
  event_id: string | null;
  actor_id: string | null;
  effective_at: string | null;
  recorded_at: string | null;
  reason: string | null;
}

export interface CanonicalBindingRecord {
  entity_kind: "binding";
  id: string;
  operation_id: string;
  artifact_version_id: string;
  direction: "input" | "output";
  role: string;
  ordinal: number;
  propagates_invalidity: boolean;
  created_at: string;
  effective_at: string;
  recorded_at: string;
  state: CanonicalBindingState;
}

export interface CanonicalBindingStateEventRecord {
  entity_kind: "binding_state_event";
  id: string;
  project_id: string;
  binding_id: string;
  operation_id: string | null;
  state: Exclude<CanonicalBindingLifecycleState, "unrecorded">;
  change_cause: CanonicalLineageChangeCause;
  actor_id: string;
  effective_at: string;
  recorded_at: string;
  updated_at: string;
  reason: string | null;
  metadata: JsonObject;
}

export interface CanonicalOperationStateEventRecord {
  entity_kind: "operation_state_event";
  id: string;
  project_id: string;
  operation_id: string;
  state: Exclude<CanonicalBindingLifecycleState, "unrecorded">;
  change_cause: CanonicalLineageChangeCause;
  actor_id: string;
  effective_at: string;
  recorded_at: string;
  updated_at: string;
  reason: string | null;
  metadata: JsonObject;
}

export interface CanonicalSupersessionRecord extends CanonicalSupersessionSummary {
  entity_kind: "supersession";
  project_id: string;
  operation_id: string | null;
  import_id: string | null;
}

export interface CanonicalMergeInputRecord {
  entity_kind: "merge_input";
  id: string;
  project_id: string;
  merge_operation_id: string;
  source_operation_id: string;
  included: boolean;
  rationale: string | null;
  created_at: string;
  effective_at: string;
  recorded_at: string;
}

export interface CanonicalTopologyRetirementRecord {
  entity_kind: "topology_retirement";
  id: string;
  project_id: string;
  retired_entity_kind:
    | "artifact"
    | "artifact_version"
    | "operation_run"
    | "binding"
    | "supersession"
    | "merge_input"
    | "decision_evidence";
  retired_entity_id: string;
  retired_entity_ordinal: number;
  retirement_type: "retired" | "withdrawn" | "superseded" | "merged_out";
  effective_at: string;
  recorded_at: string;
}

export interface CanonicalLifecycleEventRecord {
  entity_kind: "lifecycle_event";
  id: string;
  project_id: string;
  artifact_version_id: string;
  event_type:
    | "invalidated"
    | "superseded"
    | "redacted"
    | "abandoned"
    | "declared_inapplicable"
    | "reactivated";
  state:
    | "active"
    | "invalidated"
    | "superseded"
    | "redacted"
    | "abandoned"
    | "inapplicable";
  effective_at: string;
  recorded_at: string;
  actor_id: string;
  idempotency_key: string;
  import_id: string | null;
  caused_by_artifact_version_id: string | null;
  reason: string;
  source_uri: string | null;
  metadata: JsonObject;
  receipt_idempotency_key: string | null;
  receipt_request_hash: string | null;
  audit_receipt_id: string | null;
  domain_event_id: string | null;
  receipt_recorded_at: string | null;
}

export interface CanonicalDecisionStatusEventSummary {
  id: string;
  status: CanonicalDecisionStatus;
  effective_at: string;
  recorded_at: string;
  actor_id: string;
  reason: string | null;
  metadata: JsonObject;
}

export interface CanonicalDecisionRecord {
  entity_kind: "decision";
  id: string;
  project_id: string;
  import_id: string;
  decision_id: string;
  artifact_version_id: string;
  evidence_artifact_version_ids: string[];
  thread_uri: string;
  timestamp: string;
  rationale_spool_uri: string | null;
  metadata: JsonObject;
  status: CanonicalDecisionStatus;
  status_events_through_time: CanonicalDecisionStatusEventSummary[];
  created_by: string;
  created_at: string;
  effective_at: string;
  recorded_at: string;
}

export interface CanonicalDecisionEvidenceRecord {
  entity_kind: "decision_evidence";
  project_id: string;
  decision_id: string;
  ordinal: number;
  artifact_version_id: string;
  import_id: string;
  effective_at: string;
  recorded_at: string;
}

export interface CanonicalDecisionStatusEventRecord {
  entity_kind: "decision_status_event";
  id: string;
  project_id: string;
  decision_id: string;
  status: CanonicalDecisionStatus;
  effective_at: string;
  recorded_at: string;
  actor_id: string;
  reason: string | null;
  metadata: JsonObject;
  import_id: string;
}

export interface CanonicalJournalEntryRecord {
  entity_kind: "journal_entry";
  id: string;
  project_id: string;
  artifact_version_id: string;
  ordinal: number;
  actor_id: string;
  effective_at: string;
  recorded_at: string;
  thread_uri: string | null;
  metadata: JsonObject;
}

export interface CanonicalUnknownRecord {
  entity_kind: "unknown";
  id: string;
  original_type: string;
  source_table: string;
  payload: JsonObject;
  warning: "unmapped_record_type";
  temporal_basis: "unmapped_no_temporal_claim";
}

export interface CanonicalUnknownRelationship {
  relationship_kind: "unknown";
  id: string;
  source: CanonicalLineageRecordRef;
  target: CanonicalLineageRecordRef;
  original_type: string;
  source_table: string;
  payload: JsonObject;
  warning: "unmapped_relationship_type";
  temporal_basis: "unmapped_no_temporal_claim";
}

export interface CanonicalSemanticSignal {
  code:
    | "unknown_record_type"
    | "unknown_relationship_type"
    | "invalid_relation"
    | "redacted_endpoint"
    | "storage_gap";
  source_table: string;
  record_id: string | null;
  field: string | null;
  raw_value: JsonValue | null;
  message: string;
}

export interface CanonicalLineageReadModel {
  contract_version: typeof CANONICAL_LINEAGE_CONTRACT_VERSION;
  context: CanonicalLineageContext;
  artifacts: CanonicalArtifactRecord[];
  artifact_versions: CanonicalArtifactVersionRecord[];
  operation_specs: CanonicalOperationSpecRecord[];
  tool_snapshots: CanonicalToolSnapshotRecord[];
  actors: CanonicalActorRecord[];
  operation_runs: CanonicalOperationRunRecord[];
  bindings: CanonicalBindingRecord[];
  binding_state_events: CanonicalBindingStateEventRecord[];
  operation_state_events: CanonicalOperationStateEventRecord[];
  supersessions: CanonicalSupersessionRecord[];
  merge_inputs: CanonicalMergeInputRecord[];
  topology_retirements: CanonicalTopologyRetirementRecord[];
  lifecycle_events: CanonicalLifecycleEventRecord[];
  decisions: CanonicalDecisionRecord[];
  decision_evidence: CanonicalDecisionEvidenceRecord[];
  decision_status_events: CanonicalDecisionStatusEventRecord[];
  journal_entries: CanonicalJournalEntryRecord[];
  unknown_records: CanonicalUnknownRecord[];
  unknown_relationships: CanonicalUnknownRelationship[];
  semantic_signals: CanonicalSemanticSignal[];
  warnings: string[];
}

export interface CanonicalLineageEnvelope {
  schema_version: "thread.api.v1";
  request_id: string;
  data: CanonicalLineageReadModel;
  page: { next_cursor: string | null; truncated: boolean } | null;
  provenance: {
    source: "thread_core";
    projection_version: typeof CANONICAL_LINEAGE_CONTRACT_VERSION;
    schema_migration: string;
  };
  warnings: string[];
}

export class CanonicalLineageValidationError extends Error {
  readonly path: string;

  constructor(path: string, expectation: string) {
    super(`${path}: ${expectation}`);
    this.name = "CanonicalLineageValidationError";
    this.path = path;
  }
}

type UnknownRecord = Record<string, unknown>;

const DOCUMENT_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const REQUEST_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SHA256_PATTERN = /^[0-9a-f]{64}$/;
const PREFIXED_SHA256_PATTERN = /^sha256:[0-9a-f]{64}$/;
const SOURCE_CONTENT_HASH_PATTERN = /^[a-z0-9_-]+:[0-9a-f]+$/;
const AWARE_TIMESTAMP_PATTERN = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:\d{2})$/;
const AWARE_TIMESTAMP_PARTS = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(Z|(([+-])(\d{2}):(\d{2})))$/;
const FIXED_UTC_TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z$/;
const SNAPSHOT_TOKEN_PATTERN = /^ts1\.[0-9a-f]{16}\.[A-Za-z0-9_-]{39,4060}$/;
const LINEAGE_JSON_MAX_DEPTH = 32;
const LINEAGE_JSON_MAX_NODES = 4_096;
const LINEAGE_JSON_MAX_CANONICAL_BYTES = 262_144;
const LINEAGE_JSON_AGGREGATE_MAX_NODES = 32_768;
const LINEAGE_JSON_AGGREGATE_MAX_CANONICAL_BYTES = 2_097_152;
const LINEAGE_DOCUMENT_MAX_CANONICAL_BYTES = 8_388_608;

const ENTITY_KINDS = [
  "artifact",
  "artifact_version",
  "operation_spec",
  "operation_run",
  "tool",
  "actor",
  "binding",
  "binding_state_event",
  "operation_state_event",
  "supersession",
  "merge_input",
  "topology_retirement",
  "lifecycle_event",
  "decision",
  "decision_evidence",
  "decision_status_event",
  "journal_entry",
  "unknown",
] as const;

const CHANGE_CAUSES = [
  "geometry_changed",
  "mesh_changed",
  "solver_version_changed",
  "coordinate_frame_changed",
  "unit_system_changed",
  "schema_changed",
  "reference_area_changed",
  "parameter_changed",
  "metadata_only_changed",
  "comment_only_changed",
  "unknown_impact",
  "manual_override",
] as const;

const DECISION_STATUSES = [
  "proposed",
  "ratified",
  "under_review",
  "pending_ratification",
  "withdrawn",
  "superseded",
] as const;

function fail(path: string, expectation: string): never {
  throw new CanonicalLineageValidationError(path, expectation);
}

function record(value: unknown, path: string): UnknownRecord {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    fail(path, "expected an object");
  }
  return value as UnknownRecord;
}

function exactRecord(
  value: unknown,
  path: string,
  keys: readonly string[],
  optionalKeys: readonly string[] = [],
): UnknownRecord {
  const result = record(value, path);
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(result, key)) {
      fail(`${path}.${key}`, "missing required field");
    }
  }
  const expected = new Set([...keys, ...optionalKeys]);
  // Iterate lazily so an attacker-controlled object with extreme fan-out
  // cannot force a temporary key array before the first unknown field fails.
  for (const key in result) {
    if (!Object.prototype.hasOwnProperty.call(result, key)) continue;
    if (!expected.has(key)) fail(`${path}.${key}`, "unknown field on a known contract object");
  }
  return result;
}

function array(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) fail(path, "expected an array");
  return value;
}

function string(value: unknown, path: string, allowEmpty = false): string {
  if (typeof value !== "string" || (!allowEmpty && value.length === 0)) {
    fail(path, allowEmpty ? "expected a string" : "expected a non-empty string");
  }
  return value;
}

function nullableString(value: unknown, path: string, allowEmpty = true): string | null {
  return value === null ? null : string(value, path, allowEmpty);
}

function constrainedString(
  value: unknown,
  path: string,
  minLength: number,
  maxLength: number,
): string {
  const result = string(value, path, minLength === 0);
  const length = [...result].length;
  if (length < minLength || length > maxLength) {
    fail(path, `expected between ${minLength} and ${maxLength} characters`);
  }
  return result;
}

function nullableConstrainedString(
  value: unknown,
  path: string,
  minLength: number,
  maxLength: number,
): string | null {
  return value === null
    ? null
    : constrainedString(value, path, minLength, maxLength);
}

function uuid(value: unknown, path: string): string {
  const result = string(value, path);
  if (!DOCUMENT_UUID_PATTERN.test(result)) {
    fail(path, "expected a lowercase canonical UUID");
  }
  return result;
}

function nullableUuid(value: unknown, path: string): string | null {
  return value === null ? null : uuid(value, path);
}

function requestUuid(value: unknown, path: string): string {
  const result = string(value, path);
  if (!REQUEST_UUID_PATTERN.test(result)) {
    fail(path, "expected a canonically hyphenated UUID");
  }
  return result;
}

function validAwareTimestampComponents(value: string): boolean {
  const match = AWARE_TIMESTAMP_PARTS.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const offsetHour = match[8] === "Z" ? 0 : Number(match[11]);
  const offsetMinute = match[8] === "Z" ? 0 : Number(match[12]);
  const boundaryYearUsesUtc = (year !== 1 && year !== 9999) || match[8] === "Z";
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const monthLengths = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const normalizedMillis = Date.parse(value);
  const normalizedUtcYear = Number.isFinite(normalizedMillis) ? new Date(normalizedMillis).getUTCFullYear() : 0;
  return year >= 1
    && month >= 1 && month <= 12
    && day >= 1 && day <= monthLengths[month - 1]!
    && hour <= 23 && minute <= 59 && second <= 59
    && offsetHour <= 23 && offsetMinute <= 59
    && boundaryYearUsesUtc
    && normalizedUtcYear >= 1 && normalizedUtcYear <= 9999;
}

function awareTimestamp(value: unknown, path: string): string {
  const result = string(value, path);
  if (!AWARE_TIMESTAMP_PATTERN.test(result) || !validAwareTimestampComponents(result)) {
    fail(path, "expected an ISO-8601 timestamp with a timezone offset");
  }
  return result;
}

function fixedUtcTimestamp(value: unknown, path: string): string {
  const result = string(value, path);
  if (!FIXED_UTC_TIMESTAMP_PATTERN.test(result) || !validAwareTimestampComponents(result)) {
    fail(path, "expected a UTC timestamp with exactly six fractional digits");
  }
  return result;
}

function snapshotToken(value: unknown, path: string): string {
  const result = string(value, path);
  if (!SNAPSHOT_TOKEN_PATTERN.test(result)) fail(path, "expected a canonical ts1 snapshot token");
  return result;
}

function schemaMigration(value: unknown, path: string): string {
  const result = string(value, path);
  if (!/^\d{14}$/.test(result) || result < CANONICAL_LINEAGE_MIN_SCHEMA_MIGRATION) {
    fail(path, `expected a migration marker at or after ${CANONICAL_LINEAGE_MIN_SCHEMA_MIGRATION}`);
  }
  return result;
}

function validateTemporalReplayFields(item: UnknownRecord, path: string): void {
  const hasRecorded = item.recorded_as_of !== undefined;
  const hasToken = item.snapshot_token !== undefined;
  if (hasRecorded !== hasToken) fail(path, "recorded_as_of and snapshot_token must be supplied together");
  if (!hasRecorded) return;
  const recordedAsOf = fixedUtcTimestamp(item.recorded_as_of, `${path}.recorded_as_of`);
  snapshotToken(item.snapshot_token, `${path}.snapshot_token`);
  if (typeof item.as_of !== "string") fail(`${path}.as_of`, "is required when replaying a frozen snapshot");
  if (compareInstantsAtMicrosecondPrecision(item.as_of, recordedAsOf) > 0) {
    fail(`${path}.as_of`, "must not be later than recorded_as_of");
  }
}

function timestampParts(value: string): { wholeSecond: number; fraction: string } {
  const match = AWARE_TIMESTAMP_PATTERN.exec(value);
  if (match === null) {
    throw new Error("timestampParts requires a validated aware timestamp");
  }
  const wholeSecond = Date.parse(`${match[1]}${match[3]}`) / 1000;
  return { wholeSecond, fraction: match[2] ?? "" };
}

function compareAwareTimestamps(left: string, right: string): number {
  const leftParts = timestampParts(left);
  const rightParts = timestampParts(right);
  if (leftParts.wholeSecond !== rightParts.wholeSecond) {
    return leftParts.wholeSecond < rightParts.wholeSecond ? -1 : 1;
  }
  const fractionLength = Math.max(leftParts.fraction.length, rightParts.fraction.length);
  const leftFraction = leftParts.fraction.padEnd(fractionLength, "0");
  const rightFraction = rightParts.fraction.padEnd(fractionLength, "0");
  return leftFraction < rightFraction ? -1 : leftFraction > rightFraction ? 1 : 0;
}

function normalizedInstantMicros(value: string, path: string): bigint {
  if (!validAwareTimestampComponents(value)) fail(path, "expected a valid aware instant");
  const match = AWARE_TIMESTAMP_PARTS.exec(value)!;
  const wholeSecond = `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${match[6]}${match[8]}`;
  const wholeSecondMillis = Date.parse(wholeSecond);
  if (!Number.isFinite(wholeSecondMillis)) fail(path, "expected a valid aware instant");
  const micros = BigInt(`${match[7] ?? ""}000000`.slice(0, 6));
  return BigInt(wholeSecondMillis) * 1000n + micros;
}

function nullableTimestamp(value: unknown, path: string): string | null {
  return value === null ? null : fixedUtcTimestamp(value, path);
}

function boolean(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") fail(path, "expected a boolean");
  return value;
}

function integer(value: unknown, path: string, min = 0, max = Number.MAX_SAFE_INTEGER): number {
  if (!Number.isInteger(value) || typeof value !== "number" || value < min || value > max) {
    fail(path, `expected an integer between ${min} and ${max}`);
  }
  return value;
}

function literal<T extends string>(value: unknown, path: string, choices: readonly T[]): T {
  if (typeof value !== "string" || !choices.includes(value as T)) {
    fail(path, `expected one of: ${choices.join(", ")}`);
  }
  return value as T;
}

function constrainedArray(
  value: unknown,
  path: string,
  minLength: number,
  maxLength: number,
): unknown[] {
  const result = array(value, path);
  if (result.length < minLength || result.length > maxLength) {
    fail(path, `expected between ${minLength} and ${maxLength} items`);
  }
  return result;
}

interface JsonValueFrame {
  value: unknown;
  path: string;
  depth: number;
}

type CanonicalDocumentFrame =
  | { kind: "value"; value: unknown; path: string; depth: number }
  | { kind: "array"; value: unknown[]; path: string; depth: number; nextIndex: number }
  | {
    kind: "object";
    value: UnknownRecord;
    path: string;
    depth: number;
    keys: Generator<string, void, undefined>;
    memberCount: number;
  };

function* ownEnumerableKeys(value: UnknownRecord): Generator<string, void, undefined> {
  for (const key in value) {
    if (Object.prototype.hasOwnProperty.call(value, key)) yield key;
  }
}

function canonicalStringByteLength(
  value: string,
  path: string,
  maximum = Number.MAX_SAFE_INTEGER,
): number {
  let bytes = 2;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code === 0x22 || code === 0x5c || code === 0x08 || code === 0x09 || code === 0x0a || code === 0x0c || code === 0x0d) {
      bytes += 2;
    } else if (code <= 0x1f) {
      bytes += 6;
    } else if (code <= 0x7f) {
      bytes += 1;
    } else if (code <= 0x7ff) {
      bytes += 2;
    } else if (code >= 0xd800 && code <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) fail(path, "JCS cannot encode an unpaired Unicode surrogate");
      bytes += 4;
      index += 1;
    } else if (code >= 0xdc00 && code <= 0xdfff) {
      fail(path, "JCS cannot encode an unpaired Unicode surrogate");
    } else {
      bytes += 3;
    }
    if (bytes > maximum) return maximum + 1;
  }
  return bytes;
}

function validateCanonicalDocument(value: unknown, path: string): void {
  // This pass runs before exactRecord/array mapping. It measures the complete
  // lineage document with O(depth) working memory: arrays are visited by index
  // and object keys are yielded lazily rather than materialised with
  // Object.keys/Object.entries. Key order does not affect canonical byte size.
  const stack: CanonicalDocumentFrame[] = [{ kind: "value", value, path, depth: 1 }];
  const activeContainers = new WeakSet<object>();
  let bytes = 0;
  const add = (count: number) => {
    bytes += count;
    if (bytes > LINEAGE_DOCUMENT_MAX_CANONICAL_BYTES) {
      fail(path, `lineage document exceeds maximum canonical byte length ${LINEAGE_DOCUMENT_MAX_CANONICAL_BYTES}`);
    }
  };

  while (stack.length) {
    const frame = stack.pop()!;
    if (frame.kind === "array") {
      if (frame.nextIndex >= frame.value.length) {
        activeContainers.delete(frame.value);
        continue;
      }
      add(frame.nextIndex === 0 ? 0 : 1);
      const index = frame.nextIndex;
      stack.push({ ...frame, nextIndex: index + 1 });
      stack.push({
        kind: "value",
        value: frame.value[index],
        path: `${frame.path}[${index}]`,
        depth: frame.depth + 1,
      });
      continue;
    }
    if (frame.kind === "object") {
      const next = frame.keys.next();
      if (next.done) {
        activeContainers.delete(frame.value);
        continue;
      }
      const key = next.value;
      add(frame.memberCount === 0 ? 0 : 1);
      add(canonicalStringByteLength(key, `${frame.path}.<key>`, LINEAGE_DOCUMENT_MAX_CANONICAL_BYTES - bytes));
      add(1);
      stack.push({ ...frame, memberCount: frame.memberCount + 1 });
      stack.push({
        kind: "value",
        value: frame.value[key],
        path: `${frame.path}.${key}`,
        depth: frame.depth + 1,
      });
      continue;
    }

    const entry = frame.value;
    if (entry === null) {
      add(4);
    } else if (typeof entry === "boolean") {
      add(entry ? 4 : 5);
    } else if (typeof entry === "number") {
      if (!Number.isFinite(entry) || (Number.isInteger(entry) && !Number.isSafeInteger(entry))) {
        fail(frame.path, "expected a finite JSON number in the RFC 8785 safe-integer domain");
      }
      const encoded = JSON.stringify(entry);
      if (encoded === undefined) fail(frame.path, "expected a finite JSON number");
      add(encoded.length);
    } else if (typeof entry === "string") {
      add(canonicalStringByteLength(entry, frame.path, LINEAGE_DOCUMENT_MAX_CANONICAL_BYTES - bytes));
    } else if (Array.isArray(entry)) {
      if (activeContainers.has(entry)) fail(frame.path, "expected an acyclic JSON value");
      activeContainers.add(entry);
      add(2);
      stack.push({ kind: "array", value: entry, path: frame.path, depth: frame.depth, nextIndex: 0 });
    } else if (typeof entry === "object") {
      if (activeContainers.has(entry)) fail(frame.path, "expected an acyclic JSON value");
      activeContainers.add(entry);
      add(2);
      const object = entry as UnknownRecord;
      stack.push({
        kind: "object",
        value: object,
        path: frame.path,
        depth: frame.depth,
        keys: ownEnumerableKeys(object),
        memberCount: 0,
      });
    } else {
      fail(frame.path, "expected a JSON value");
    }
  }
}

function validateJsonValueStructure(value: unknown, path: string): number {
  // Match the server resource accounting: the root is depth one; every value,
  // container, and object member name is a node. Member names do not add depth.
  const stack: JsonValueFrame[] = [{ value, path, depth: 1 }];
  let nodes = 0;
  while (stack.length) {
    const frame = stack.pop()!;
    nodes += 1;
    if (frame.depth > LINEAGE_JSON_MAX_DEPTH) {
      fail(frame.path, `lineage JSON exceeds maximum depth ${LINEAGE_JSON_MAX_DEPTH}`);
    }
    if (nodes > LINEAGE_JSON_MAX_NODES) {
      fail(path, `lineage JSON exceeds maximum node count ${LINEAGE_JSON_MAX_NODES}`);
    }
    const entry = frame.value;
    if (entry === null || typeof entry === "boolean") continue;
    if (typeof entry === "string") {
      if (canonicalStringByteLength(entry, frame.path, LINEAGE_JSON_MAX_CANONICAL_BYTES) > LINEAGE_JSON_MAX_CANONICAL_BYTES) {
        fail(path, `lineage JSON exceeds maximum canonical byte length ${LINEAGE_JSON_MAX_CANONICAL_BYTES}`);
      }
      continue;
    }
    if (typeof entry === "number") {
      if (!Number.isFinite(entry) || (Number.isInteger(entry) && !Number.isSafeInteger(entry))) {
        fail(frame.path, "expected a finite JSON number in the RFC 8785 safe-integer domain");
      }
      continue;
    }
    if (Array.isArray(entry)) {
      if (nodes + stack.length + entry.length > LINEAGE_JSON_MAX_NODES) {
        fail(path, `lineage JSON exceeds maximum node count ${LINEAGE_JSON_MAX_NODES}`);
      }
      for (let index = entry.length - 1; index >= 0; index -= 1) {
        stack.push({ value: entry[index], path: `${frame.path}[${index}]`, depth: frame.depth + 1 });
      }
      continue;
    }
    if (typeof entry === "object") {
      const keys: string[] = [];
      for (const key in entry as UnknownRecord) {
        if (!Object.prototype.hasOwnProperty.call(entry, key)) continue;
        if (nodes + stack.length + (keys.length + 1) * 2 > LINEAGE_JSON_MAX_NODES) {
          fail(path, `lineage JSON exceeds maximum node count ${LINEAGE_JSON_MAX_NODES}`);
        }
        if (canonicalStringByteLength(key, `${frame.path}.<key>`, LINEAGE_JSON_MAX_CANONICAL_BYTES) > LINEAGE_JSON_MAX_CANONICAL_BYTES) {
          fail(path, `lineage JSON exceeds maximum canonical byte length ${LINEAGE_JSON_MAX_CANONICAL_BYTES}`);
        }
        keys.push(key);
      }
      nodes += keys.length;
      for (let index = keys.length - 1; index >= 0; index -= 1) {
        const key = keys[index]!;
        stack.push({ value: (entry as UnknownRecord)[key], path: `${frame.path}.${key}`, depth: frame.depth + 1 });
      }
      continue;
    }
    fail(frame.path, "expected a JSON value");
  }
  return nodes;
}

function canonicalJsonByteLength(value: unknown, path: string): number {
  // Structure admission has already capped fan-out, so this second iterative
  // pass can enumerate admitted keys without risking attacker-sized arrays.
  const stack: Array<{ value: unknown; path: string }> = [{ value, path }];
  let bytes = 0;
  const add = (count: number) => {
    bytes += count;
    if (bytes > LINEAGE_JSON_MAX_CANONICAL_BYTES) {
      fail(path, `lineage JSON exceeds maximum canonical byte length ${LINEAGE_JSON_MAX_CANONICAL_BYTES}`);
    }
  };
  while (stack.length) {
    const frame = stack.pop()!;
    const entry = frame.value;
    if (entry === null) {
      add(4);
    } else if (typeof entry === "boolean") {
      add(entry ? 4 : 5);
    } else if (typeof entry === "number") {
      const encoded = JSON.stringify(entry);
      if (encoded === undefined) fail(frame.path, "expected a finite JSON number");
      add(encoded.length);
    } else if (typeof entry === "string") {
      add(canonicalStringByteLength(entry, frame.path, LINEAGE_JSON_MAX_CANONICAL_BYTES - bytes));
    } else if (Array.isArray(entry)) {
      add(2 + Math.max(0, entry.length - 1));
      for (let index = entry.length - 1; index >= 0; index -= 1) {
        stack.push({ value: entry[index], path: `${frame.path}[${index}]` });
      }
    } else if (typeof entry === "object") {
      const keys = Object.keys(entry);
      add(2 + Math.max(0, keys.length - 1) + keys.length);
      for (let index = keys.length - 1; index >= 0; index -= 1) {
        const key = keys[index]!;
        add(canonicalStringByteLength(key, `${frame.path}.<key>`, LINEAGE_JSON_MAX_CANONICAL_BYTES - bytes));
        stack.push({ value: (entry as UnknownRecord)[key], path: `${frame.path}.${key}` });
      }
    } else {
      fail(frame.path, "expected a JSON value");
    }
  }
  return bytes;
}

function jsonValue(value: unknown, path: string): asserts value is JsonValue {
  validateJsonValueStructure(value, path);
  canonicalJsonByteLength(value, path);
}

export interface CanonicalLineageJsonResourceEntry {
  value: unknown;
  path: string;
}

export interface CanonicalLineageJsonAggregateUsage {
  nodes: number;
  canonicalBytes: number;
}

/**
 * Admit an exact, lazily supplied set of semantic JSON fields under the
 * lineage aggregate envelope. The final wire-specific field selector remains
 * separate, so schema evolution cannot silently change the resource policy.
 */
export function validateCanonicalLineageJsonAggregate(
  entries: Iterable<CanonicalLineageJsonResourceEntry>,
): CanonicalLineageJsonAggregateUsage {
  let nodes = 0;
  let canonicalBytes = 0;
  for (const entry of entries) {
    nodes += validateJsonValueStructure(entry.value, entry.path);
    if (nodes > LINEAGE_JSON_AGGREGATE_MAX_NODES) {
      fail(entry.path, `lineage semantic JSON exceeds aggregate node count ${LINEAGE_JSON_AGGREGATE_MAX_NODES}`);
    }
    canonicalBytes += canonicalJsonByteLength(entry.value, entry.path);
    if (canonicalBytes > LINEAGE_JSON_AGGREGATE_MAX_CANONICAL_BYTES) {
      fail(entry.path, `lineage semantic JSON exceeds aggregate canonical byte length ${LINEAGE_JSON_AGGREGATE_MAX_CANONICAL_BYTES}`);
    }
  }
  return { nodes, canonicalBytes };
}

function jsonObject(value: unknown, path: string): JsonObject {
  const result = record(value, path);
  jsonValue(result, path);
  return result as JsonObject;
}

function stringArray(value: unknown, path: string, validator = string): string[] {
  return array(value, path).map((item, index) => validator(item, `${path}[${index}]`));
}

function validateLineageWarnings(value: unknown, path: string): void {
  constrainedArray(value, path, 0, 100).forEach((entry, index) => {
    constrainedString(entry, `${path}[${index}]`, 1, 500);
  });
}

function validateRecordRef(value: unknown, path: string): void {
  const item = exactRecord(value, path, ["entity_kind", "id"]);
  literal(item.entity_kind, `${path}.entity_kind`, ENTITY_KINDS);
  uuid(item.id, `${path}.id`);
}

function validateBoundary(value: unknown, path: string): void {
  const item = exactRecord(value, path, ["entity_kind", "id", "reason", "detail"]);
  literal(item.entity_kind, `${path}.entity_kind`, ENTITY_KINDS);
  uuid(item.id, `${path}.id`);
  literal(item.reason, `${path}.reason`, ["outside_projection", "not_found", "unsupported"]);
  nullableConstrainedString(item.detail, `${path}.detail`, 1, 500);
}

function validateProjectionLimits(value: unknown, path: string): void {
  const item = exactRecord(value, path, [
    "resource_limits_version", "max_depth", "max_records", "max_paths", "max_boundaries",
    "json_max_depth", "json_max_nodes", "json_field_max_canonical_bytes",
    "json_aggregate_max_nodes", "json_aggregate_max_canonical_bytes", "document_max_canonical_bytes",
  ]);
  literal(item.resource_limits_version, `${path}.resource_limits_version`, ["thread.lineage-resource-limits.v2"]);
  integer(item.max_depth, `${path}.max_depth`, 1, 32);
  integer(item.max_records, `${path}.max_records`, 1, 500);
  integer(item.max_paths, `${path}.max_paths`, 1, 5000);
  const fixed: ReadonlyArray<readonly [string, number]> = [
    ["max_boundaries", 10_000],
    ["json_max_depth", 32],
    ["json_max_nodes", 4_096],
    ["json_field_max_canonical_bytes", 262_144],
    ["json_aggregate_max_nodes", 32_768],
    ["json_aggregate_max_canonical_bytes", 2_097_152],
    ["document_max_canonical_bytes", 8_388_608],
  ];
  for (const [key, expected] of fixed) {
    if (integer(item[key], `${path}.${key}`) !== expected) fail(`${path}.${key}`, `expected ${expected}`);
  }
}

function validateContext(value: unknown, path: string): void {
  const item = exactRecord(value, path, [
    "project_id", "projection", "generated_at", "as_of", "recorded_as_of", "snapshot_token",
    "temporal_history", "limits", "root_ids", "boundary", "truncated",
  ]);
  uuid(item.project_id, `${path}.project_id`);
  const projection = literal(item.projection, `${path}.projection`, ["snapshot", "neighbourhood", "trace", "search", "focus", "hierarchy", "summary", "dsm"]);
  const generatedAt = fixedUtcTimestamp(item.generated_at, `${path}.generated_at`);
  const asOf = fixedUtcTimestamp(item.as_of, `${path}.as_of`);
  const recordedAsOf = fixedUtcTimestamp(item.recorded_as_of, `${path}.recorded_as_of`);
  snapshotToken(item.snapshot_token, `${path}.snapshot_token`);
  const temporalHistory = exactRecord(item.temporal_history, `${path}.temporal_history`, ["status", "marker"]);
  const historyStatus = literal(
    temporalHistory.status,
    `${path}.temporal_history.status`,
    ["complete", "incomplete"],
  );
  literal(
    temporalHistory.marker,
    `${path}.temporal_history.marker`,
    [historyStatus === "complete" ? "temporal_history_complete" : "temporal_history_incomplete"],
  );
  validateProjectionLimits(item.limits, `${path}.limits`);
  const limits = item.limits as CanonicalLineageContext["limits"];
  if (normalizedInstantMicros(generatedAt, `${path}.generated_at`) !== normalizedInstantMicros(recordedAsOf, `${path}.recorded_as_of`)) {
    fail(`${path}.generated_at`, "must equal recorded_as_of");
  }
  if (compareInstantsAtMicrosecondPrecision(asOf, recordedAsOf) > 0) {
    fail(`${path}.as_of`, "must not be later than recorded_as_of");
  }
  const rootIds = stringArray(item.root_ids, `${path}.root_ids`, uuid);
  if (rootIds.length > limits.max_records) {
    fail(`${path}.root_ids`, "lineage roots exceed selected max_records");
  }
  const canonicalRootIds = [...new Set(rootIds.map((id) => id.toLowerCase()))].sort();
  if (!sameStrings(rootIds.map((id) => id.toLowerCase()), canonicalRootIds)) {
    fail(`${path}.root_ids`, "must be unique and canonically sorted");
  }
  const boundary = array(item.boundary, `${path}.boundary`);
  if (boundary.length > 10_000) fail(`${path}.boundary`, "must contain at most 10000 items");
  boundary.forEach((entry, index) => validateBoundary(entry, `${path}.boundary[${index}]`));
  const boundaryKeys = boundary.map((entry) => {
    const record = entry as CanonicalLineageBoundary;
    return `${record.entity_kind}:${record.id.toLowerCase()}`;
  });
  const canonicalBoundaryKeys = [...boundaryKeys].sort();
  if (!sameStrings(boundaryKeys, canonicalBoundaryKeys)) {
    fail(`${path}.boundary`, "must use canonical entity-kind and ID order");
  }
  const truncated = boolean(item.truncated, `${path}.truncated`);
  if (boundary.length > 0 && !truncated) {
    fail(`${path}.truncated`, "must be true when a projection boundary is reported");
  }
  if (historyStatus === "incomplete" && !truncated) {
    fail(`${path}.truncated`, "must be true when temporal history is incomplete");
  }
  if (
    historyStatus === "complete"
    && (projection === "neighbourhood" || projection === "focus" || projection === "trace")
    && rootIds.length === 0
  ) {
    fail(`${path}.root_ids`, "root-bearing lineage projections require at least one root");
  }
  if (historyStatus === "complete" && projection === "focus" && rootIds.length !== 1) {
    fail(`${path}.root_ids`, "focus lineage projection requires exactly one root");
  }
}

function validateModelInstance(value: unknown, path: string): void {
  const item = exactRecord(value, path, ["classification", "generic_artifact_id"]);
  const classification = literal(item.classification, `${path}.classification`, ["generic", "instance", "unclassified"]);
  const genericId = nullableUuid(item.generic_artifact_id, `${path}.generic_artifact_id`);
  if ((classification === "instance") !== (genericId !== null)) {
    fail(path, "instance classification must carry generic_artifact_id, and other classifications must not");
  }
}

function validateContentIdentity(value: unknown, path: string): void {
  const item = exactRecord(value, path, [
    "algorithm", "digest", "canonicalizer", "canonicalizer_version", "byte_length", "media_type", "source_content_hash",
  ]);
  literal(item.algorithm, `${path}.algorithm`, ["sha256"]);
  const digest = string(item.digest, `${path}.digest`);
  if (!SHA256_PATTERN.test(digest)) fail(`${path}.digest`, "expected 64 lowercase hexadecimal characters");
  const canonicalizer = nullableString(item.canonicalizer, `${path}.canonicalizer`);
  const canonicalizerVersion = nullableString(item.canonicalizer_version, `${path}.canonicalizer_version`);
  if ((canonicalizer === null) !== (canonicalizerVersion === null)) {
    fail(path, "canonicalizer and canonicalizer_version must both be null or both be present");
  }
  if (item.byte_length !== null) integer(item.byte_length, `${path}.byte_length`);
  constrainedString(item.media_type, `${path}.media_type`, 1, 255);
  if (item.source_content_hash !== null) {
    const sourceContentHash = string(
      item.source_content_hash,
      `${path}.source_content_hash`,
    );
    if (!SOURCE_CONTENT_HASH_PATTERN.test(sourceContentHash)) {
      fail(
        `${path}.source_content_hash`,
        "expected <lowercase algorithm>:<lowercase hexadecimal digest>",
      );
    }
  }
}

function validateArtifact(value: unknown, path: string): void {
  const item = exactRecord(value, path, [
    "id", "logical_id", "project_id", "kind", "name", "sensitivity_tier_id", "sensitivity_tier_name",
    "metadata", "created_by", "created_at", "effective_at", "recorded_at", "entity_kind", "model_instance",
  ]);
  literal(item.entity_kind, `${path}.entity_kind`, ["artifact"]);
  ["id", "logical_id", "project_id", "created_by"].forEach((key) => uuid(item[key], `${path}.${key}`));
  constrainedString(item.kind, `${path}.kind`, 1, 100);
  constrainedString(item.name, `${path}.name`, 1, 300);
  uuid(item.sensitivity_tier_id, `${path}.sensitivity_tier_id`);
  constrainedString(
    item.sensitivity_tier_name,
    `${path}.sensitivity_tier_name`,
    1,
    100,
  );
  jsonObject(item.metadata, `${path}.metadata`);
  fixedUtcTimestamp(item.created_at, `${path}.created_at`);
  fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
  fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
  validateModelInstance(item.model_instance, `${path}.model_instance`);
}

const SUPERSESSION_KEYS = [
  "id", "old_artifact_version_id", "new_artifact_version_id", "change_type", "reason", "created_by", "created_at",
  "effective_at", "recorded_at",
] as const;

function validateSupersessionSummary(value: unknown, path: string, entityKind: boolean): void {
  const keys = entityKind
    ? [...SUPERSESSION_KEYS, "entity_kind", "project_id", "operation_id", "import_id"]
    : SUPERSESSION_KEYS;
  const item = exactRecord(value, path, keys);
  if (entityKind) {
    literal(item.entity_kind, `${path}.entity_kind`, ["supersession"]);
    uuid(item.project_id, `${path}.project_id`);
    nullableUuid(item.operation_id, `${path}.operation_id`);
    nullableUuid(item.import_id, `${path}.import_id`);
  }
  ["id", "old_artifact_version_id", "new_artifact_version_id", "created_by"].forEach((key) => uuid(item[key], `${path}.${key}`));
  literal(item.change_type, `${path}.change_type`, CHANGE_CAUSES);
  nullableConstrainedString(item.reason, `${path}.reason`, 1, 2000);
  fixedUtcTimestamp(item.created_at, `${path}.created_at`);
  fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
  fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
}

function validateArtifactVersion(value: unknown, path: string): void {
  const item = exactRecord(value, path, [
    "id", "artifact_id", "project_id", "sensitivity_tier_id", "sensitivity_tier_name", "content_identity",
    "structured_content", "metadata", "schema_id", "unit_context_id", "coordinate_frame_id", "blob_ref",
    "structured_value_ref", "lifecycle_state", "considered_valid_at_time", "authoritative_for_new_work_at_time",
    "lifecycle_basis", "created_by", "created_at", "effective_at", "recorded_at", "entity_kind",
  ]);
  literal(item.entity_kind, `${path}.entity_kind`, ["artifact_version"]);
  ["id", "artifact_id", "project_id", "created_by"].forEach((key) => uuid(item[key], `${path}.${key}`));
  uuid(item.sensitivity_tier_id, `${path}.sensitivity_tier_id`);
  constrainedString(
    item.sensitivity_tier_name,
    `${path}.sensitivity_tier_name`,
    1,
    100,
  );
  validateContentIdentity(item.content_identity, `${path}.content_identity`);
  jsonValue(item.structured_content, `${path}.structured_content`);
  jsonObject(item.metadata, `${path}.metadata`);
  ["schema_id", "unit_context_id", "coordinate_frame_id"].forEach((key) => nullableUuid(item[key], `${path}.${key}`));
  ["blob_ref", "structured_value_ref"].forEach((key) => (
    nullableConstrainedString(item[key], `${path}.${key}`, 0, 2000)
  ));
  const lifecycleState = literal(item.lifecycle_state, `${path}.lifecycle_state`, [
    "active", "invalidated", "superseded", "redacted", "abandoned", "inapplicable",
  ]);
  const consideredValid = boolean(
    item.considered_valid_at_time,
    `${path}.considered_valid_at_time`,
  );
  const authoritativeForNewWork = boolean(
    item.authoritative_for_new_work_at_time,
    `${path}.authoritative_for_new_work_at_time`,
  );
  const invalidStates = new Set([
    "invalidated", "superseded", "redacted", "abandoned", "inapplicable",
  ]);
  if (consideredValid !== !invalidStates.has(lifecycleState)) {
    fail(
      `${path}.considered_valid_at_time`,
      "must equal the validity implied by lifecycle_state",
    );
  }
  if (authoritativeForNewWork !== (lifecycleState === "active")) {
    fail(
      `${path}.authoritative_for_new_work_at_time`,
      "must be true exactly when lifecycle_state is active",
    );
  }
  literal(item.lifecycle_basis, `${path}.lifecycle_basis`, ["initial_active_no_lifecycle_event", "derived_from_exact_lifecycle_timeline", "independent_state_selector"]);
  fixedUtcTimestamp(item.created_at, `${path}.created_at`);
  fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
  fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
}

function validateRoleContract(value: unknown, path: string): void {
  const item = exactRecord(value, path, ["role", "required", "min_count", "max_count"]);
  constrainedString(item.role, `${path}.role`, 1, 100);
  boolean(item.required, `${path}.required`);
  const min = integer(item.min_count, `${path}.min_count`, 0, 1000);
  const max = integer(item.max_count, `${path}.max_count`, 1, 1000);
  if (min > max) fail(path, "min_count must not exceed max_count");
  if (item.required && min < 1) fail(path, "required roles need min_count >= 1");
}

function validateOperationSpec(value: unknown, path: string): void {
  const item = exactRecord(value, path, [
    "id", "name", "version", "operation_type", "parameter_schema", "input_roles", "output_roles", "definition_hash",
    "created_at", "effective_at", "recorded_at", "entity_kind",
  ]);
  literal(item.entity_kind, `${path}.entity_kind`, ["operation_spec"]);
  uuid(item.id, `${path}.id`);
  constrainedString(item.name, `${path}.name`, 1, 128);
  integer(item.version, `${path}.version`, 1);
  constrainedString(item.operation_type, `${path}.operation_type`, 1, 100);
  jsonObject(item.parameter_schema, `${path}.parameter_schema`);
  const inputRoles = constrainedArray(item.input_roles, `${path}.input_roles`, 0, 100);
  const outputRoles = constrainedArray(item.output_roles, `${path}.output_roles`, 1, 100);
  inputRoles.forEach((entry, index) => validateRoleContract(entry, `${path}.input_roles[${index}]`));
  outputRoles.forEach((entry, index) => validateRoleContract(entry, `${path}.output_roles[${index}]`));
  for (const [direction, roles] of [
    ["input", inputRoles],
    ["output", outputRoles],
  ] as const) {
    const names = roles.map((role) => (role as CanonicalRoleContract).role);
    if (new Set(names).size !== names.length) {
      fail(`${path}.${direction}_roles`, "role names must be unique within this direction");
    }
  }
  const hash = string(item.definition_hash, `${path}.definition_hash`);
  if (!PREFIXED_SHA256_PATTERN.test(hash)) fail(`${path}.definition_hash`, "expected sha256:<64 lowercase hex>");
  fixedUtcTimestamp(item.created_at, `${path}.created_at`);
  fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
  fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
}

function validateTool(value: unknown, path: string): void {
  const item = exactRecord(value, path, [
    "name", "version", "build_id", "source_ref", "metadata", "entity_kind", "id", "definition_hash",
    "temporal_basis", "owning_operation_ids",
  ]);
  literal(item.entity_kind, `${path}.entity_kind`, ["tool"]);
  uuid(item.id, `${path}.id`);
  constrainedString(item.name, `${path}.name`, 1, 200);
  constrainedString(item.version, `${path}.version`, 1, 200);
  nullableConstrainedString(item.build_id, `${path}.build_id`, 0, 500);
  nullableConstrainedString(item.source_ref, `${path}.source_ref`, 0, 2000);
  jsonObject(item.metadata, `${path}.metadata`);
  const hash = string(item.definition_hash, `${path}.definition_hash`);
  if (!PREFIXED_SHA256_PATTERN.test(hash)) fail(`${path}.definition_hash`, "expected sha256:<64 lowercase hex>");
  literal(item.temporal_basis, `${path}.temporal_basis`, ["derived_from_operation_run"]);
  const owners = stringArray(item.owning_operation_ids, `${path}.owning_operation_ids`, uuid);
  if (owners.length < 1 || owners.length > 500) fail(`${path}.owning_operation_ids`, "expected between 1 and 500 owners");
  if (owners.some((id, index) => index > 0 && owners[index - 1]! >= id)) fail(`${path}.owning_operation_ids`, "must be sorted and unique");
}

function validateActor(value: unknown, path: string): void {
  const item = exactRecord(value, path, ["entity_kind", "id", "actor_type", "resolution", "name", "role", "metadata", "temporal_basis"]);
  literal(item.entity_kind, `${path}.entity_kind`, ["actor"]);
  uuid(item.id, `${path}.id`);
  literal(item.actor_type, `${path}.actor_type`, ["user", "service", "imported", "unknown"]);
  const resolution = literal(item.resolution, `${path}.resolution`, ["resolved", "id_only", "redacted"]);
  const name = nullableConstrainedString(item.name, `${path}.name`, 0, 300);
  const role = nullableConstrainedString(item.role, `${path}.role`, 0, 300);
  const metadata = jsonObject(item.metadata, `${path}.metadata`);
  if (resolution !== "resolved" && (name !== null || role !== null)) {
    fail(path, "id-only and redacted actors cannot carry display identity");
  }
  if (resolution === "redacted" && Object.keys(metadata).length > 0) {
    fail(path, "redacted actors cannot carry metadata");
  }
  literal(item.temporal_basis, `${path}.temporal_basis`, ["current_authorization_overlay"]);
}

function validateEnvironment(value: unknown, path: string): void {
  const item = exactRecord(value, path, ["runtime", "operating_system", "architecture", "container_digest", "dependencies", "metadata"]);
  constrainedString(item.runtime, `${path}.runtime`, 1, 300);
  nullableConstrainedString(
    item.operating_system,
    `${path}.operating_system`,
    0,
    300,
  );
  nullableConstrainedString(item.architecture, `${path}.architecture`, 0, 100);
  nullableConstrainedString(
    item.container_digest,
    `${path}.container_digest`,
    0,
    500,
  );
  const dependencies = record(item.dependencies, `${path}.dependencies`);
  for (const key in dependencies) {
    if (!Object.prototype.hasOwnProperty.call(dependencies, key)) continue;
    string(dependencies[key], `${path}.dependencies.${key}`, true);
  }
  jsonObject(item.metadata, `${path}.metadata`);
}

function validateAssumption(value: unknown, path: string): void {
  const item = exactRecord(value, path, ["name", "statement", "reference"]);
  constrainedString(item.name, `${path}.name`, 1, 300);
  constrainedString(item.statement, `${path}.statement`, 1, 4000);
  nullableConstrainedString(item.reference, `${path}.reference`, 0, 2000);
}

function validateOperationRun(value: unknown, path: string): void {
  const item = exactRecord(value, path, [
    "entity_kind", "id", "project_id", "operation_spec_id", "actor_id", "tool_id", "idempotency_key", "parameters",
    "environment", "assumptions", "config_hash", "source_ref", "metadata", "request_hash", "audit_receipt_id",
    "domain_event_id", "started_at", "completed_at", "status", "created_at", "effective_at", "recorded_at",
  ]);
  literal(item.entity_kind, `${path}.entity_kind`, ["operation_run"]);
  ["id", "project_id", "operation_spec_id", "actor_id", "tool_id"].forEach((key) => uuid(item[key], `${path}.${key}`));
  constrainedString(item.idempotency_key, `${path}.idempotency_key`, 1, 200);
  jsonValue(item.parameters, `${path}.parameters`);
  validateEnvironment(item.environment, `${path}.environment`);
  constrainedArray(item.assumptions, `${path}.assumptions`, 0, 100).forEach(
    (entry, index) => validateAssumption(entry, `${path}.assumptions[${index}]`),
  );
  nullableConstrainedString(item.config_hash, `${path}.config_hash`, 0, 500);
  nullableConstrainedString(item.source_ref, `${path}.source_ref`, 0, 2000);
  jsonObject(item.metadata, `${path}.metadata`);
  const requestHash = string(item.request_hash, `${path}.request_hash`);
  if (!PREFIXED_SHA256_PATTERN.test(requestHash)) fail(`${path}.request_hash`, "expected sha256:<64 lowercase hex>");
  const auditReceiptId = nullableUuid(item.audit_receipt_id, `${path}.audit_receipt_id`);
  const domainEventId = nullableUuid(item.domain_event_id, `${path}.domain_event_id`);
  if ((auditReceiptId === null) !== (domainEventId === null)) {
    fail(path, "operation audit and domain receipt IDs must be present or null together");
  }
  const started = fixedUtcTimestamp(item.started_at, `${path}.started_at`);
  const completed = fixedUtcTimestamp(item.completed_at, `${path}.completed_at`);
  if (compareAwareTimestamps(completed, started) < 0) fail(path, "completed_at must not precede started_at");
  constrainedString(item.status, `${path}.status`, 1, 100);
  fixedUtcTimestamp(item.created_at, `${path}.created_at`);
  fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
  fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
}

function validateBindingState(value: unknown, path: string): void {
  const item = exactRecord(value, path, ["state", "basis", "change_cause", "event_id", "actor_id", "effective_at", "recorded_at", "reason"]);
  const state = literal(item.state, `${path}.state`, ["unrecorded", "fresh", "stale", "invalidated", "superseded", "pinned", "deferred"]);
  const basis = literal(item.basis, `${path}.basis`, ["explicit_event", "unrecorded"]);
  const cause = item.change_cause === null ? null : literal(item.change_cause, `${path}.change_cause`, CHANGE_CAUSES);
  const eventId = nullableUuid(item.event_id, `${path}.event_id`);
  const actorId = nullableUuid(item.actor_id, `${path}.actor_id`);
  const effectiveAt = nullableTimestamp(item.effective_at, `${path}.effective_at`);
  const recordedAt = nullableTimestamp(item.recorded_at, `${path}.recorded_at`);
  nullableConstrainedString(item.reason, `${path}.reason`, 1, 2000);
  if (basis === "unrecorded") {
    if (state !== "unrecorded" || cause !== null || eventId !== null || item.actor_id !== null || item.effective_at !== null || item.recorded_at !== null || item.reason !== null) {
      fail(path, "unrecorded binding state cannot carry an event payload");
    }
  } else if (
    state === "unrecorded"
    || cause === null
    || eventId === null
    || actorId === null
    || effectiveAt === null
    || recordedAt === null
  ) {
    fail(path, "explicit binding state requires a recorded state, event, cause, actor, and both clocks");
  }
}

function validateBinding(value: unknown, path: string): void {
  const item = exactRecord(value, path, [
    "entity_kind", "id", "operation_id", "artifact_version_id", "direction", "role", "ordinal",
    "propagates_invalidity", "created_at", "effective_at", "recorded_at", "state",
  ]);
  literal(item.entity_kind, `${path}.entity_kind`, ["binding"]);
  ["id", "operation_id", "artifact_version_id"].forEach((key) => uuid(item[key], `${path}.${key}`));
  const direction = literal(item.direction, `${path}.direction`, ["input", "output"]);
  constrainedString(item.role, `${path}.role`, 1, 100);
  integer(item.ordinal, `${path}.ordinal`);
  const propagates = boolean(item.propagates_invalidity, `${path}.propagates_invalidity`);
  if (direction === "output" && propagates) fail(`${path}.propagates_invalidity`, "output bindings cannot propagate invalidity");
  fixedUtcTimestamp(item.created_at, `${path}.created_at`);
  fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
  fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
  validateBindingState(item.state, `${path}.state`);
}

function validateBindingStateEvent(value: unknown, path: string): void {
  const item = exactRecord(value, path, ["entity_kind", "id", "project_id", "binding_id", "operation_id", "state", "change_cause", "actor_id", "effective_at", "recorded_at", "updated_at", "reason", "metadata"]);
  literal(item.entity_kind, `${path}.entity_kind`, ["binding_state_event"]);
  ["id", "project_id", "binding_id", "actor_id"].forEach((key) => uuid(item[key], `${path}.${key}`));
  nullableUuid(item.operation_id, `${path}.operation_id`);
  literal(item.state, `${path}.state`, ["fresh", "stale", "invalidated", "superseded", "pinned", "deferred"]);
  literal(item.change_cause, `${path}.change_cause`, CHANGE_CAUSES);
  fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
  fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
  fixedUtcTimestamp(item.updated_at, `${path}.updated_at`);
  nullableConstrainedString(item.reason, `${path}.reason`, 1, 2000);
  jsonObject(item.metadata, `${path}.metadata`);
}

function validateOperationStateEvent(value: unknown, path: string): void {
  const item = exactRecord(value, path, ["entity_kind", "id", "project_id", "operation_id", "state", "change_cause", "actor_id", "effective_at", "recorded_at", "updated_at", "reason", "metadata"]);
  literal(item.entity_kind, `${path}.entity_kind`, ["operation_state_event"]);
  ["id", "project_id", "operation_id", "actor_id"].forEach((key) => uuid(item[key], `${path}.${key}`));
  literal(item.state, `${path}.state`, ["fresh", "stale", "invalidated", "superseded", "pinned", "deferred"]);
  literal(item.change_cause, `${path}.change_cause`, CHANGE_CAUSES);
  fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
  fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
  fixedUtcTimestamp(item.updated_at, `${path}.updated_at`);
  nullableConstrainedString(item.reason, `${path}.reason`, 1, 2000);
  jsonObject(item.metadata, `${path}.metadata`);
}

function validateMergeInput(value: unknown, path: string): void {
  const item = exactRecord(value, path, [
    "entity_kind", "id", "project_id", "merge_operation_id", "source_operation_id", "included", "rationale",
    "created_at", "effective_at", "recorded_at",
  ]);
  literal(item.entity_kind, `${path}.entity_kind`, ["merge_input"]);
  ["id", "project_id", "merge_operation_id", "source_operation_id"].forEach((key) => uuid(item[key], `${path}.${key}`));
  if (item.merge_operation_id === item.source_operation_id) fail(path, "merge operation cannot select itself");
  boolean(item.included, `${path}.included`);
  nullableConstrainedString(item.rationale, `${path}.rationale`, 1, 2000);
  fixedUtcTimestamp(item.created_at, `${path}.created_at`);
  fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
  fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
}

function validateTopologyRetirement(value: unknown, path: string): void {
  const item = exactRecord(value, path, [
    "entity_kind", "id", "project_id", "retired_entity_kind", "retired_entity_id",
    "retired_entity_ordinal", "retirement_type", "effective_at", "recorded_at",
  ]);
  literal(item.entity_kind, `${path}.entity_kind`, ["topology_retirement"]);
  ["id", "project_id", "retired_entity_id"].forEach((key) => uuid(item[key], `${path}.${key}`));
  const retiredKind = literal(item.retired_entity_kind, `${path}.retired_entity_kind`, [
    "artifact", "artifact_version", "operation_run", "binding", "supersession", "merge_input", "decision_evidence",
  ]);
  const ordinal = integer(item.retired_entity_ordinal, `${path}.retired_entity_ordinal`, -1);
  if ((retiredKind === "decision_evidence") !== (ordinal >= 0)) {
    fail(`${path}.retired_entity_ordinal`, "decision evidence requires a non-negative ordinal; other targets require -1");
  }
  literal(item.retirement_type, `${path}.retirement_type`, ["retired", "withdrawn", "superseded", "merged_out"]);
  fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
  fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
}

function validateLifecycleEvent(value: unknown, path: string): void {
  const item = exactRecord(value, path, [
    "entity_kind", "id", "project_id", "artifact_version_id", "event_type", "state", "effective_at", "recorded_at",
    "actor_id", "idempotency_key", "import_id", "caused_by_artifact_version_id", "reason", "source_uri", "metadata", "receipt_idempotency_key",
    "receipt_request_hash", "audit_receipt_id", "domain_event_id", "receipt_recorded_at",
  ]);
  literal(item.entity_kind, `${path}.entity_kind`, ["lifecycle_event"]);
  ["id", "project_id", "artifact_version_id", "actor_id"].forEach((key) => uuid(item[key], `${path}.${key}`));
  const eventType = literal(item.event_type, `${path}.event_type`, ["invalidated", "superseded", "redacted", "abandoned", "declared_inapplicable", "reactivated"]);
  const state = literal(item.state, `${path}.state`, ["active", "invalidated", "superseded", "redacted", "abandoned", "inapplicable"]);
  const expectedState = eventType === "reactivated"
    ? "active"
    : eventType === "declared_inapplicable"
      ? "inapplicable"
      : eventType;
  if (state !== expectedState) {
    fail(path, "lifecycle state must be the canonical state for its event_type");
  }
  fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
  const recordedAt = fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
  constrainedString(item.idempotency_key, `${path}.idempotency_key`, 1, 200);
  const importId = nullableUuid(item.import_id, `${path}.import_id`);
  nullableUuid(item.caused_by_artifact_version_id, `${path}.caused_by_artifact_version_id`);
  constrainedString(item.reason, `${path}.reason`, 1, 2000);
  nullableConstrainedString(item.source_uri, `${path}.source_uri`, 0, 2000);
  jsonObject(item.metadata, `${path}.metadata`);
  const receiptKey = nullableConstrainedString(
    item.receipt_idempotency_key,
    `${path}.receipt_idempotency_key`,
    1,
    200,
  );
  let receiptHash: string | null = null;
  if (item.receipt_request_hash !== null) {
    receiptHash = string(item.receipt_request_hash, `${path}.receipt_request_hash`);
    if (!PREFIXED_SHA256_PATTERN.test(receiptHash)) {
      fail(`${path}.receipt_request_hash`, "expected sha256:<64 lowercase hex>");
    }
  }
  const auditReceiptId = nullableUuid(item.audit_receipt_id, `${path}.audit_receipt_id`);
  const domainEventId = nullableUuid(item.domain_event_id, `${path}.domain_event_id`);
  const receiptRecordedAt = nullableTimestamp(
    item.receipt_recorded_at,
    `${path}.receipt_recorded_at`,
  );
  const receiptValues = [
    receiptKey,
    receiptHash,
    auditReceiptId,
    domainEventId,
    receiptRecordedAt,
  ];
  if (receiptValues.some((entry) => entry !== null) && receiptValues.some((entry) => entry === null)) {
    fail(path, "lifecycle receipt fields must be present or null together");
  }
  if (importId !== null && receiptValues.some((entry) => entry !== null)) {
    fail(path, "imported lifecycle events cannot carry a native invalidation receipt");
  }
  if (receiptKey !== null && receiptKey !== item.idempotency_key) {
    fail(path, "lifecycle receipt idempotency must match the lifecycle event");
  }
  if (receiptKey !== null && (eventType !== "invalidated" || state !== "invalidated")) {
    fail(path, "native invalidation receipts require an invalidated lifecycle event");
  }
  if (receiptRecordedAt !== null && compareAwareTimestamps(receiptRecordedAt, recordedAt) < 0) {
    fail(`${path}.receipt_recorded_at`, "must not precede lifecycle recorded_at");
  }
}

function validateDecisionStatusSummary(value: unknown, path: string): void {
  const item = exactRecord(value, path, ["id", "status", "effective_at", "recorded_at", "actor_id", "reason", "metadata"]);
  uuid(item.id, `${path}.id`);
  literal(item.status, `${path}.status`, DECISION_STATUSES);
  fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
  fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
  uuid(item.actor_id, `${path}.actor_id`);
  nullableConstrainedString(item.reason, `${path}.reason`, 1, 2000);
  jsonObject(item.metadata, `${path}.metadata`);
}

function validateDecision(value: unknown, path: string): void {
  const item = exactRecord(value, path, [
    "id", "project_id", "import_id", "decision_id", "artifact_version_id", "evidence_artifact_version_ids",
    "thread_uri", "timestamp", "rationale_spool_uri", "metadata", "status", "status_events_through_time",
    "created_by", "created_at", "effective_at", "recorded_at", "entity_kind",
  ]);
  literal(item.entity_kind, `${path}.entity_kind`, ["decision"]);
  ["id", "project_id", "import_id", "artifact_version_id", "created_by"].forEach((key) => uuid(item[key], `${path}.${key}`));
  constrainedString(item.decision_id, `${path}.decision_id`, 1, 200);
  const evidenceIds = stringArray(
    item.evidence_artifact_version_ids,
    `${path}.evidence_artifact_version_ids`,
    uuid,
  );
  if (evidenceIds.length > 1000) {
    fail(`${path}.evidence_artifact_version_ids`, "must contain at most 1000 items");
  }
  constrainedString(item.thread_uri, `${path}.thread_uri`, 1, 2000);
  fixedUtcTimestamp(item.timestamp, `${path}.timestamp`);
  nullableConstrainedString(
    item.rationale_spool_uri,
    `${path}.rationale_spool_uri`,
    0,
    2000,
  );
  jsonObject(item.metadata, `${path}.metadata`);
  literal(item.status, `${path}.status`, DECISION_STATUSES);
  constrainedArray(
    item.status_events_through_time,
    `${path}.status_events_through_time`,
    1,
    100,
  ).forEach((entry, index) => (
    validateDecisionStatusSummary(entry, `${path}.status_events_through_time[${index}]`)
  ));
  fixedUtcTimestamp(item.created_at, `${path}.created_at`);
  fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
  fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
}

function validateDecisionEvidence(value: unknown, path: string): void {
  const item = exactRecord(value, path, ["entity_kind", "project_id", "decision_id", "ordinal", "artifact_version_id", "import_id", "effective_at", "recorded_at"]);
  literal(item.entity_kind, `${path}.entity_kind`, ["decision_evidence"]);
  uuid(item.project_id, `${path}.project_id`);
  uuid(item.decision_id, `${path}.decision_id`);
  integer(item.ordinal, `${path}.ordinal`);
  uuid(item.artifact_version_id, `${path}.artifact_version_id`);
  uuid(item.import_id, `${path}.import_id`);
  fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
  fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
}

function validateDecisionStatusEvent(value: unknown, path: string): void {
  const item = exactRecord(value, path, ["entity_kind", "id", "project_id", "decision_id", "status", "effective_at", "recorded_at", "actor_id", "reason", "metadata", "import_id"]);
  literal(item.entity_kind, `${path}.entity_kind`, ["decision_status_event"]);
  ["id", "project_id", "decision_id", "actor_id", "import_id"].forEach((key) => uuid(item[key], `${path}.${key}`));
  literal(item.status, `${path}.status`, DECISION_STATUSES);
  fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
  fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
  nullableConstrainedString(item.reason, `${path}.reason`, 1, 2000);
  jsonObject(item.metadata, `${path}.metadata`);
}

function validateJournalEntry(value: unknown, path: string): void {
  const item = exactRecord(value, path, ["entity_kind", "id", "project_id", "artifact_version_id", "ordinal", "actor_id", "effective_at", "recorded_at", "thread_uri", "metadata"]);
  literal(item.entity_kind, `${path}.entity_kind`, ["journal_entry"]);
  ["id", "project_id", "artifact_version_id", "actor_id"].forEach((key) => uuid(item[key], `${path}.${key}`));
  integer(item.ordinal, `${path}.ordinal`);
  fixedUtcTimestamp(item.effective_at, `${path}.effective_at`);
  fixedUtcTimestamp(item.recorded_at, `${path}.recorded_at`);
  nullableConstrainedString(item.thread_uri, `${path}.thread_uri`, 0, 2000);
  jsonObject(item.metadata, `${path}.metadata`);
}

function validateUnknownRecord(value: unknown, path: string): void {
  const item = exactRecord(value, path, ["entity_kind", "id", "original_type", "source_table", "payload", "warning", "temporal_basis"]);
  literal(item.entity_kind, `${path}.entity_kind`, ["unknown"]);
  uuid(item.id, `${path}.id`);
  constrainedString(item.original_type, `${path}.original_type`, 1, 300);
  constrainedString(item.source_table, `${path}.source_table`, 1, 300);
  jsonObject(item.payload, `${path}.payload`);
  literal(item.warning, `${path}.warning`, ["unmapped_record_type"]);
  literal(item.temporal_basis, `${path}.temporal_basis`, ["unmapped_no_temporal_claim"]);
}

function validateUnknownRelationship(value: unknown, path: string): void {
  const item = exactRecord(value, path, ["relationship_kind", "id", "source", "target", "original_type", "source_table", "payload", "warning", "temporal_basis"]);
  literal(item.relationship_kind, `${path}.relationship_kind`, ["unknown"]);
  uuid(item.id, `${path}.id`);
  validateRecordRef(item.source, `${path}.source`);
  validateRecordRef(item.target, `${path}.target`);
  constrainedString(item.original_type, `${path}.original_type`, 1, 300);
  constrainedString(item.source_table, `${path}.source_table`, 1, 300);
  jsonObject(item.payload, `${path}.payload`);
  literal(item.warning, `${path}.warning`, ["unmapped_relationship_type"]);
  literal(item.temporal_basis, `${path}.temporal_basis`, ["unmapped_no_temporal_claim"]);
}

function validateSemanticSignal(value: unknown, path: string): void {
  const item = exactRecord(value, path, ["code", "source_table", "record_id", "field", "raw_value", "message"]);
  literal(item.code, `${path}.code`, ["unknown_record_type", "unknown_relationship_type", "invalid_relation", "redacted_endpoint", "storage_gap"]);
  constrainedString(item.source_table, `${path}.source_table`, 1, 300);
  nullableUuid(item.record_id, `${path}.record_id`);
  nullableConstrainedString(item.field, `${path}.field`, 1, 300);
  jsonValue(item.raw_value, `${path}.raw_value`);
  constrainedString(item.message, `${path}.message`, 1, 1000);
}

type CollectionValidator = (value: unknown, path: string) => void;

const COLLECTION_VALIDATORS: ReadonlyArray<readonly [keyof CanonicalLineageReadModel, CollectionValidator]> = [
  ["artifacts", validateArtifact],
  ["artifact_versions", validateArtifactVersion],
  ["operation_specs", validateOperationSpec],
  ["tool_snapshots", validateTool],
  ["actors", validateActor],
  ["operation_runs", validateOperationRun],
  ["bindings", validateBinding],
  ["binding_state_events", validateBindingStateEvent],
  ["operation_state_events", validateOperationStateEvent],
  ["supersessions", (value, path) => validateSupersessionSummary(value, path, true)],
  ["merge_inputs", validateMergeInput],
  ["topology_retirements", validateTopologyRetirement],
  ["lifecycle_events", validateLifecycleEvent],
  ["decisions", validateDecision],
  ["decision_evidence", validateDecisionEvidence],
  ["decision_status_events", validateDecisionStatusEvent],
  ["journal_entries", validateJournalEntry],
  ["unknown_records", validateUnknownRecord],
  ["unknown_relationships", validateUnknownRelationship],
  ["semantic_signals", validateSemanticSignal],
];

function validateCollectionIdentities(model: CanonicalLineageReadModel, path: string): void {
  const keyedCollections: ReadonlyArray<readonly [string, ReadonlyArray<{ id: string }>]> = [
    ["artifacts", model.artifacts],
    ["artifact_versions", model.artifact_versions],
    ["operation_specs", model.operation_specs],
    ["tool_snapshots", model.tool_snapshots],
    ["actors", model.actors],
    ["operation_runs", model.operation_runs],
    ["bindings", model.bindings],
    ["binding_state_events", model.binding_state_events],
    ["operation_state_events", model.operation_state_events],
    ["supersessions", model.supersessions],
    ["merge_inputs", model.merge_inputs],
    ["topology_retirements", model.topology_retirements],
    ["lifecycle_events", model.lifecycle_events],
    ["decisions", model.decisions],
    ["decision_status_events", model.decision_status_events],
    ["journal_entries", model.journal_entries],
    ["unknown_records", model.unknown_records],
    ["unknown_relationships", model.unknown_relationships],
  ];
  keyedCollections.forEach(([name, items]) => {
    const ids = items.map((item) => item.id);
    if (new Set(ids).size !== ids.length) fail(`${path}.${name}`, "record IDs must be unique");
  });
  const invalidationStateEventIds = [
    ...model.binding_state_events.map((item) => item.id),
    ...model.operation_state_events.map((item) => item.id),
  ];
  if (new Set(invalidationStateEventIds).size !== invalidationStateEventIds.length) {
    fail(`${path}.operation_state_events`, "invalidation state-event IDs must be unique across target collections");
  }
  const evidenceSlots = model.decision_evidence.map((item) => `${item.decision_id}:${item.ordinal}`);
  if (new Set(evidenceSlots).size !== evidenceSlots.length) fail(`${path}.decision_evidence`, "decision evidence slots must be unique");
  const bindingSlots = model.bindings.map((item) => (
    `${item.operation_id}:${item.direction}:${item.role}:${item.ordinal}`
  ));
  if (new Set(bindingSlots).size !== bindingSlots.length) {
    fail(`${path}.bindings`, "operation binding slots must be unique");
  }
  const boundaryKeys = model.context.boundary.map((item) => referenceKey(item.entity_kind, item.id));
  if (new Set(boundaryKeys).size !== boundaryKeys.length) {
    fail(`${path}.context.boundary`, "projection boundary identities must be unique");
  }
}

function referenceKey(kind: CanonicalLineageEntityKind, id: string): string {
  return `${kind}:${id}`;
}

function sameStrings(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function sameJsonValue(left: unknown, right: unknown): boolean {
  if (left === right) return true;
  if (Array.isArray(left) || Array.isArray(right)) {
    return Array.isArray(left)
      && Array.isArray(right)
      && left.length === right.length
      && left.every((value, index) => sameJsonValue(value, right[index]));
  }
  if (
    left === null
    || right === null
    || typeof left !== "object"
    || typeof right !== "object"
  ) {
    return false;
  }
  const leftRecord = left as UnknownRecord;
  const rightRecord = right as UnknownRecord;
  const leftKeys = Object.keys(leftRecord).sort();
  const rightKeys = Object.keys(rightRecord).sort();
  return sameStrings(leftKeys, rightKeys)
    && leftKeys.every((key) => sameJsonValue(leftRecord[key], rightRecord[key]));
}

function sameNullableTimestamp(left: string | null, right: string | null): boolean {
  return left === null || right === null
    ? left === right
    : compareAwareTimestamps(left, right) === 0;
}

function compareLifecycleEvents(
  left: CanonicalLifecycleEventRecord,
  right: CanonicalLifecycleEventRecord,
): number {
  const effectiveDifference = compareAwareTimestamps(left.effective_at, right.effective_at);
  if (effectiveDifference !== 0) return effectiveDifference;
  const recordedDifference = compareAwareTimestamps(left.recorded_at, right.recorded_at);
  if (recordedDifference !== 0) return recordedDifference;
  const leftId = left.id.toLowerCase();
  const rightId = right.id.toLowerCase();
  return leftId < rightId ? -1 : leftId > rightId ? 1 : 0;
}

function compareBindingStateEvents(
  left: CanonicalBindingStateEventRecord,
  right: CanonicalBindingStateEventRecord,
): number {
  const bindingDifference = left.binding_id.toLowerCase().localeCompare(right.binding_id.toLowerCase());
  if (bindingDifference !== 0) return bindingDifference;
  const effectiveDifference = compareAwareTimestamps(left.effective_at, right.effective_at);
  if (effectiveDifference !== 0) return effectiveDifference;
  const recordedDifference = compareAwareTimestamps(left.recorded_at, right.recorded_at);
  if (recordedDifference !== 0) return recordedDifference;
  return left.id.toLowerCase().localeCompare(right.id.toLowerCase());
}

function compareOperationStateEvents(
  left: CanonicalOperationStateEventRecord,
  right: CanonicalOperationStateEventRecord,
): number {
  const operationDifference = left.operation_id.toLowerCase().localeCompare(right.operation_id.toLowerCase());
  if (operationDifference !== 0) return operationDifference;
  const effectiveDifference = compareAwareTimestamps(left.effective_at, right.effective_at);
  if (effectiveDifference !== 0) return effectiveDifference;
  const recordedDifference = compareAwareTimestamps(left.recorded_at, right.recorded_at);
  if (recordedDifference !== 0) return recordedDifference;
  return left.id.toLowerCase().localeCompare(right.id.toLowerCase());
}

function validateReferenceIntegrity(model: CanonicalLineageReadModel, path: string): void {
  const projectScopedCollections: ReadonlyArray<
    readonly [string, ReadonlyArray<{ project_id: string }>]
  > = [
    ["artifacts", model.artifacts],
    ["artifact_versions", model.artifact_versions],
    ["operation_runs", model.operation_runs],
    ["binding_state_events", model.binding_state_events],
    ["operation_state_events", model.operation_state_events],
    ["supersessions", model.supersessions],
    ["merge_inputs", model.merge_inputs],
    ["topology_retirements", model.topology_retirements],
    ["lifecycle_events", model.lifecycle_events],
    ["decisions", model.decisions],
    ["decision_evidence", model.decision_evidence],
    ["decision_status_events", model.decision_status_events],
    ["journal_entries", model.journal_entries],
  ];
  projectScopedCollections.forEach(([name, records]) => {
    records.forEach((record, index) => {
      if (record.project_id !== model.context.project_id) {
        fail(`${path}.${name}[${index}].project_id`, "must equal context.project_id");
      }
    });
  });
  if (canonicalRecordCount(model) > model.context.limits.max_records) {
    fail(path, "normalized records exceed context.limits.max_records");
  }
  if (model.context.boundary.length > model.context.limits.max_boundaries) {
    fail(`${path}.context.boundary`, "exceeds context.limits.max_boundaries");
  }
  if (model.binding_state_events.some((event, index, events) => (
    index > 0 && compareBindingStateEvents(events[index - 1]!, event) > 0
  ))) {
    fail(
      `${path}.binding_state_events`,
      "must be ordered by binding, effective_at, recorded_at, and id",
    );
  }
  if (model.operation_state_events.some((event, index, events) => (
    index > 0 && compareOperationStateEvents(events[index - 1]!, event) > 0
  ))) {
    fail(
      `${path}.operation_state_events`,
      "must be ordered by operation, effective_at, recorded_at, and id",
    );
  }
  const factRecords: ReadonlyArray<{ effective_at: string | null; recorded_at: string }> = [
    ...model.artifacts,
    ...model.artifact_versions,
    ...model.operation_specs,
    ...model.operation_runs,
    ...model.bindings,
    ...model.binding_state_events,
    ...model.operation_state_events,
    ...model.supersessions,
    ...model.merge_inputs,
    ...model.topology_retirements,
    ...model.lifecycle_events,
    ...model.decisions,
    ...model.decision_evidence,
    ...model.decision_status_events,
    ...model.journal_entries,
  ];
  if (factRecords.some((record) => record.effective_at !== null
    && compareAwareTimestamps(record.effective_at, model.context.as_of) > 0)) {
    fail(path, "fact effective_at must not exceed context.as_of");
  }
  if (factRecords.some((record) => compareAwareTimestamps(record.recorded_at, model.context.recorded_as_of) > 0)) {
    fail(path, "fact recorded_at must not exceed context.recorded_as_of");
  }
  if (model.lifecycle_events.some((event) => event.receipt_recorded_at !== null
    && compareAwareTimestamps(event.receipt_recorded_at, model.context.recorded_as_of) > 0)) {
    fail(`${path}.lifecycle_events`, "receipt_recorded_at must not exceed context.recorded_as_of");
  }

  const boundary = new Set(
    model.context.boundary.map((item) => referenceKey(item.entity_kind, item.id)),
  );
  const artifacts = new Set(model.artifacts.map((item) => item.id));
  const versions = new Set(model.artifact_versions.map((item) => item.id));
  const specs = new Set(model.operation_specs.map((item) => item.id));
  const tools = new Set(model.tool_snapshots.map((item) => item.id));
  const actors = new Set(model.actors.map((item) => item.id));
  const operations = new Set(model.operation_runs.map((item) => item.id));
  const operationById = new Map(model.operation_runs.map((item) => [item.id, item]));
  const specById = new Map(model.operation_specs.map((item) => [item.id, item]));
  const bindings = new Set(model.bindings.map((item) => item.id));
  const bindingStateEvents = new Map(
    model.binding_state_events.map((item) => [item.id, item]),
  );
  const bindingStateEventsByBinding = new Map<
    string,
    CanonicalBindingStateEventRecord[]
  >();
  model.binding_state_events.forEach((event) => {
    const events = bindingStateEventsByBinding.get(event.binding_id) ?? [];
    events.push(event);
    bindingStateEventsByBinding.set(event.binding_id, events);
  });
  const operationStateEvents = new Map(
    model.operation_state_events.map((item) => [item.id, item]),
  );
  const decisions = new Set(model.decisions.map((item) => item.id));

  model.tool_snapshots.forEach((tool, index) => {
    tool.owning_operation_ids.forEach((operationId, ownerIndex) => {
      if (operationById.get(operationId)?.tool_id !== tool.id) {
        fail(`${path}.tool_snapshots[${index}].owning_operation_ids[${ownerIndex}]`, "must identify an included OperationRun using this tool");
      }
    });
  });

  const requireReference = (
    kind: CanonicalLineageEntityKind,
    id: string,
    present: ReadonlySet<string>,
    referencePath: string,
  ): void => {
    if (!present.has(id) && !boundary.has(referenceKey(kind, id))) {
      fail(referencePath, `unreported ${kind} reference requires a typed boundary`);
    }
  };

  model.context.root_ids.forEach((rootId, index) => {
    if (!versions.has(rootId)) {
      fail(
        `${path}.context.root_ids[${index}]`,
        "must identify a selected artifact_version record",
      );
    }
  });

  const lifecycleEventsByVersion = new Map<string, CanonicalLifecycleEventRecord[]>();
  model.lifecycle_events.forEach((event) => {
    const events = lifecycleEventsByVersion.get(event.artifact_version_id) ?? [];
    events.push(event);
    lifecycleEventsByVersion.set(event.artifact_version_id, events);
  });
  lifecycleEventsByVersion.forEach((events) => {
    const orderedEvents = [...events].sort(compareLifecycleEvents);
    if (!sameStrings(
      events.map((event) => event.id),
      orderedEvents.map((event) => event.id),
    )) {
      fail(
        `${path}.lifecycle_events`,
        "must be ordered by effective_at, recorded_at, and id within each artifact version",
      );
    }
  });

  model.artifact_versions.forEach((version, index) => {
    const itemPath = `${path}.artifact_versions[${index}]`;
    requireReference("artifact", version.artifact_id, artifacts, `${itemPath}.artifact_id`);
    requireReference("actor", version.created_by, actors, `${itemPath}.created_by`);
    const lifecycleEvents = lifecycleEventsByVersion.get(version.id) ?? [];
    if (version.lifecycle_basis === "derived_from_exact_lifecycle_timeline") {
      const latestEvent = lifecycleEvents[lifecycleEvents.length - 1];
      if (latestEvent === undefined || version.lifecycle_state !== latestEvent.state) {
        fail(
          `${itemPath}.lifecycle_state`,
          "derived lifecycle state requires and must match its normalized timeline",
        );
      }
    } else if (version.lifecycle_basis === "initial_active_no_lifecycle_event"
      && (lifecycleEvents.length > 0 || version.lifecycle_state !== "active")) {
      fail(
        `${itemPath}.lifecycle_basis`,
        "initial lifecycle basis requires active state and no normalized event",
      );
    }
  });
  model.artifacts.forEach((artifact, index) => {
    const itemPath = `${path}.artifacts[${index}]`;
    requireReference("actor", artifact.created_by, actors, `${itemPath}.created_by`);
    if (artifact.model_instance.generic_artifact_id !== null) {
      requireReference(
        "artifact",
        artifact.model_instance.generic_artifact_id,
        artifacts,
        `${itemPath}.model_instance.generic_artifact_id`,
      );
      const generic = model.artifacts.find(
        (candidate) => candidate.id === artifact.model_instance.generic_artifact_id,
      );
      const genericBoundary = model.context.boundary.find((candidate) => (
        candidate.entity_kind === "artifact"
        && candidate.id === artifact.model_instance.generic_artifact_id
      ));
      if (generic && generic.model_instance.classification !== "generic") {
        fail(
          `${itemPath}.model_instance.generic_artifact_id`,
          "must identify an included Artifact explicitly classified as generic",
        );
      }
      if (!generic && genericBoundary?.reason !== "outside_projection") {
        fail(
          `${itemPath}.model_instance.generic_artifact_id`,
          "an omitted generic Artifact must use an outside_projection typed boundary",
        );
      }
    }
  });
  model.operation_runs.forEach((operation, index) => {
    const itemPath = `${path}.operation_runs[${index}]`;
    requireReference(
      "operation_spec",
      operation.operation_spec_id,
      specs,
      `${itemPath}.operation_spec_id`,
    );
    requireReference("tool", operation.tool_id, tools, `${itemPath}.tool_id`);
    requireReference("actor", operation.actor_id, actors, `${itemPath}.actor_id`);
  });
  model.bindings.forEach((binding, index) => {
    const itemPath = `${path}.bindings[${index}]`;
    requireReference(
      "operation_run",
      binding.operation_id,
      operations,
      `${itemPath}.operation_id`,
    );
    requireReference(
      "artifact_version",
      binding.artifact_version_id,
      versions,
      `${itemPath}.artifact_version_id`,
    );
    const operation = operationById.get(binding.operation_id);
    const spec = operation === undefined ? undefined : specById.get(operation.operation_spec_id);
    if (spec !== undefined) {
      const roles = binding.direction === "input" ? spec.input_roles : spec.output_roles;
      if (!roles.some((contract) => contract.role === binding.role)) {
        fail(`${itemPath}.role`, "must be declared by the selected operation specification");
      }
    }
    if (binding.state.actor_id !== null) {
      requireReference("actor", binding.state.actor_id, actors, `${itemPath}.state.actor_id`);
    }
    if (binding.state.event_id !== null) {
      const event = bindingStateEvents.get(binding.state.event_id);
      if (event === undefined) {
        fail(
          `${itemPath}.state.event_id`,
          "explicit current state event must be included in the normalized timeline",
        );
      }
      const visibleTimeline = bindingStateEventsByBinding.get(binding.id) ?? [];
      if (visibleTimeline[visibleTimeline.length - 1] !== event) {
        fail(
          `${itemPath}.state.event_id`,
          "must identify the latest visible normalized event for this binding",
        );
      }
      if (
        event.binding_id !== binding.id
        || event.state !== binding.state.state
        || event.change_cause !== binding.state.change_cause
        || event.actor_id !== binding.state.actor_id
        || !sameNullableTimestamp(event.effective_at, binding.state.effective_at)
        || !sameNullableTimestamp(event.recorded_at, binding.state.recorded_at)
      ) {
        fail(`${itemPath}.state`, "must match its normalized binding_state_event");
      }
    }
  });
  model.binding_state_events.forEach((event, index) => {
    const itemPath = `${path}.binding_state_events[${index}]`;
    requireReference("binding", event.binding_id, bindings, `${itemPath}.binding_id`);
    requireReference("actor", event.actor_id, actors, `${itemPath}.actor_id`);
    if (event.operation_id !== null) {
      requireReference(
        "operation_run",
        event.operation_id,
        operations,
        `${itemPath}.operation_id`,
      );
      const owningBinding = model.bindings.find((binding) => binding.id === event.binding_id);
      if (owningBinding !== undefined && event.operation_id !== owningBinding.operation_id) {
        fail(
          `${itemPath}.operation_id`,
          "dual-target binding-state event operation must match its Binding",
        );
      }
    }
  });
  model.operation_state_events.forEach((event, index) => {
    const itemPath = `${path}.operation_state_events[${index}]`;
    requireReference(
      "operation_run",
      event.operation_id,
      operations,
      `${itemPath}.operation_id`,
    );
    requireReference("actor", event.actor_id, actors, `${itemPath}.actor_id`);
  });
  model.supersessions.forEach((edge, index) => {
    const itemPath = `${path}.supersessions[${index}]`;
    requireReference(
      "artifact_version",
      edge.old_artifact_version_id,
      versions,
      `${itemPath}.old_artifact_version_id`,
    );
    requireReference(
      "artifact_version",
      edge.new_artifact_version_id,
      versions,
      `${itemPath}.new_artifact_version_id`,
    );
    requireReference("actor", edge.created_by, actors, `${itemPath}.created_by`);
    if (edge.operation_id !== null) {
      requireReference(
        "operation_run",
        edge.operation_id,
        operations,
        `${itemPath}.operation_id`,
      );
    }
  });
  model.merge_inputs.forEach((mergeInput, index) => {
    const itemPath = `${path}.merge_inputs[${index}]`;
    requireReference(
      "operation_run",
      mergeInput.merge_operation_id,
      operations,
      `${itemPath}.merge_operation_id`,
    );
    requireReference(
      "operation_run",
      mergeInput.source_operation_id,
      operations,
      `${itemPath}.source_operation_id`,
    );
  });
  const reportedRetirementTargets: Record<
    Exclude<CanonicalTopologyRetirementRecord["retired_entity_kind"], "decision_evidence" | "operation_run" | "binding">,
    ReadonlySet<string>
  > = {
    artifact: artifacts,
    artifact_version: versions,
    supersession: new Set(model.supersessions.map((item) => item.id)),
    merge_input: new Set(model.merge_inputs.map((item) => item.id)),
  };
  model.topology_retirements.forEach((retirement, index) => {
    const itemPath = `${path}.topology_retirements[${index}].retired_entity_id`;
    if (retirement.retired_entity_kind === "decision_evidence") {
      // At/after retirement the exact evidence membership is intentionally
      // absent; the typed (decision id, ordinal) retirement tuple is evidence.
      requireReference("decision", retirement.retired_entity_id, decisions, itemPath);
    } else if (retirement.retired_entity_kind !== "operation_run" && retirement.retired_entity_kind !== "binding") {
      requireReference(
        retirement.retired_entity_kind,
        retirement.retired_entity_id,
        reportedRetirementTargets[retirement.retired_entity_kind],
        itemPath,
      );
    }
    // Retired OperationRun and Binding records alone are suppressed by the
    // cut-aware selectors. Their typed retirement tuples stand alone without
    // fabricating a retained target or a misleading omission boundary.
  });
  model.lifecycle_events.forEach((event, index) => {
    const itemPath = `${path}.lifecycle_events[${index}]`;
    requireReference(
      "artifact_version",
      event.artifact_version_id,
      versions,
      `${itemPath}.artifact_version_id`,
    );
    if (event.caused_by_artifact_version_id !== null) {
      requireReference(
        "artifact_version",
        event.caused_by_artifact_version_id,
        versions,
        `${itemPath}.caused_by_artifact_version_id`,
      );
    }
    requireReference("actor", event.actor_id, actors, `${itemPath}.actor_id`);
  });
  model.decisions.forEach((decision, index) => {
    const itemPath = `${path}.decisions[${index}]`;
    requireReference(
      "artifact_version",
      decision.artifact_version_id,
      versions,
      `${itemPath}.artifact_version_id`,
    );
    requireReference("actor", decision.created_by, actors, `${itemPath}.created_by`);
  });
  model.decision_evidence.forEach((evidence, index) => {
    const itemPath = `${path}.decision_evidence[${index}]`;
    requireReference("decision", evidence.decision_id, decisions, `${itemPath}.decision_id`);
    requireReference(
      "artifact_version",
      evidence.artifact_version_id,
      versions,
      `${itemPath}.artifact_version_id`,
    );
  });
  model.decision_status_events.forEach((event, index) => {
    const itemPath = `${path}.decision_status_events[${index}]`;
    requireReference("decision", event.decision_id, decisions, `${itemPath}.decision_id`);
    requireReference("actor", event.actor_id, actors, `${itemPath}.actor_id`);
  });
  model.journal_entries.forEach((entry, index) => {
    const itemPath = `${path}.journal_entries[${index}]`;
    requireReference(
      "artifact_version",
      entry.artifact_version_id,
      versions,
      `${itemPath}.artifact_version_id`,
    );
    requireReference("actor", entry.actor_id, actors, `${itemPath}.actor_id`);
  });

  const identitiesByKind: Record<CanonicalLineageEntityKind, ReadonlySet<string>> = {
    artifact: artifacts,
    artifact_version: versions,
    operation_spec: specs,
    operation_run: operations,
    tool: tools,
    actor: actors,
    binding: bindings,
    binding_state_event: new Set(bindingStateEvents.keys()),
    operation_state_event: new Set(operationStateEvents.keys()),
    supersession: new Set(model.supersessions.map((item) => item.id)),
    merge_input: new Set(model.merge_inputs.map((item) => item.id)),
    topology_retirement: new Set(model.topology_retirements.map((item) => item.id)),
    lifecycle_event: new Set(model.lifecycle_events.map((item) => item.id)),
    decision: decisions,
    decision_evidence: new Set(),
    decision_status_event: new Set(model.decision_status_events.map((item) => item.id)),
    journal_entry: new Set(model.journal_entries.map((item) => item.id)),
    unknown: new Set(model.unknown_records.map((item) => item.id)),
  };
  model.context.boundary.forEach((item, index) => {
    if (identitiesByKind[item.entity_kind].has(item.id)) {
      fail(
        `${path}.context.boundary[${index}]`,
        "projection boundary cannot overlap a present record",
      );
    }
  });
  model.unknown_relationships.forEach((relationship, index) => {
    (["source", "target"] as const).forEach((endpointName) => {
      const endpoint = relationship[endpointName];
      requireReference(
        endpoint.entity_kind,
        endpoint.id,
        identitiesByKind[endpoint.entity_kind],
        `${path}.unknown_relationships[${index}].${endpointName}`,
      );
    });
  });

  const includedRecordIds = new Set(
    [
      ...Object.values(identitiesByKind).flatMap((ids) => [...ids]),
      ...model.unknown_relationships.map((relationship) => relationship.id),
    ],
  );
  const boundaryIds = new Set(model.context.boundary.map((item) => item.id));
  model.semantic_signals.forEach((signal, index) => {
    if (
      signal.record_id !== null
      && !includedRecordIds.has(signal.record_id)
      && !boundaryIds.has(signal.record_id)
    ) {
      fail(
        `${path}.semantic_signals[${index}].record_id`,
        "must identify an included record or boundary",
      );
    }
  });

  model.decisions.forEach((decision, decisionIndex) => {
    const evidence = model.decision_evidence
      .filter((item) => item.decision_id === decision.id)
      .sort((left, right) => left.ordinal - right.ordinal);
    if (!sameStrings(
      evidence.map((item) => item.artifact_version_id),
      decision.evidence_artifact_version_ids,
    )) {
      fail(
        `${path}.decisions[${decisionIndex}].evidence_artifact_version_ids`,
        "must match decision_evidence ordinal order",
      );
    }
    if (evidence.some((item) => item.import_id !== decision.import_id)) {
      fail(
        `${path}.decision_evidence`,
        "decision evidence import lineage must match its decision",
      );
    }

    const statuses = model.decision_status_events
      .filter((item) => item.decision_id === decision.id)
      .sort((left, right) => {
        const timeDifference = compareAwareTimestamps(left.effective_at, right.effective_at);
        if (timeDifference !== 0) return timeDifference;
        const recordedDifference = compareAwareTimestamps(left.recorded_at, right.recorded_at);
        return recordedDifference === 0 ? left.id.localeCompare(right.id) : recordedDifference;
      });
    const statusSummariesMatch = statuses.length === decision.status_events_through_time.length
      && statuses.every((status, index) => {
        const summary = decision.status_events_through_time[index];
        return summary !== undefined
          && status.id === summary.id
          && status.status === summary.status
          && compareAwareTimestamps(status.effective_at, summary.effective_at) === 0
          && compareAwareTimestamps(status.recorded_at, summary.recorded_at) === 0
          && status.actor_id === summary.actor_id
          && status.reason === summary.reason
          && sameJsonValue(status.metadata, summary.metadata);
      });
    if (!statusSummariesMatch) {
      fail(
        `${path}.decisions[${decisionIndex}].status_events_through_time`,
        "must equal decision_status_events in effective-time order",
      );
    }
    if (statuses.some((item) => item.import_id !== decision.import_id)) {
      fail(
        `${path}.decision_status_events`,
        "decision status import lineage must match its decision",
      );
    }
    const latestStatus = statuses[statuses.length - 1];
    if (latestStatus === undefined || decision.status !== latestStatus.status) {
      fail(
        `${path}.decisions[${decisionIndex}].status`,
        "must equal the latest normalized as-of decision status",
      );
    }
  });
}

/**
 * Validate a raw lineage payload without normalising or dropping fields.
 *
 * Known records are strict and malformed values throw with an exact JSON path.
 * Unmapped records and relationships remain intact in their explicit carrier
 * arrays, including their original payloads and semantic signals.
 */
export function parseCanonicalLineage(value: unknown): CanonicalLineageReadModel {
  validateCanonicalDocument(value, "$");
  const keys = ["contract_version", "context", ...COLLECTION_VALIDATORS.map(([key]) => key), "warnings"];
  const item = exactRecord(value, "$", keys);
  literal(item.contract_version, "$.contract_version", [CANONICAL_LINEAGE_CONTRACT_VERSION]);
  validateContext(item.context, "$.context");
  COLLECTION_VALIDATORS.forEach(([key, validator]) => {
    const maxLength = key === "semantic_signals" ? 1000 : 500;
    constrainedArray(item[key], `$.${key}`, 0, maxLength).forEach(
      (entry, index) => validator(entry, `$.${key}[${index}]`),
    );
  });
  validateLineageWarnings(item.warnings, "$.warnings");

  const parsed = value as CanonicalLineageReadModel;
  validateCanonicalLineageJsonAggregate([
    ...parsed.artifacts.map((record, index) => ({ path: `$.artifacts[${index}].metadata`, value: record.metadata })),
    ...parsed.artifact_versions.flatMap((record, index) => [
      ...(record.structured_content === null ? [] : [{ path: `$.artifact_versions[${index}].structured_content`, value: record.structured_content }]),
      { path: `$.artifact_versions[${index}].metadata`, value: record.metadata },
    ]),
    ...parsed.operation_specs.map((record, index) => ({ path: `$.operation_specs[${index}].parameter_schema`, value: record.parameter_schema })),
    ...parsed.tool_snapshots.map((record, index) => ({ path: `$.tool_snapshots[${index}].metadata`, value: record.metadata })),
    ...parsed.actors.map((record, index) => ({ path: `$.actors[${index}].metadata`, value: record.metadata })),
    ...parsed.operation_runs.flatMap((record, index) => [
      { path: `$.operation_runs[${index}].parameters`, value: record.parameters },
      { path: `$.operation_runs[${index}].environment.dependencies`, value: record.environment.dependencies },
      { path: `$.operation_runs[${index}].environment.metadata`, value: record.environment.metadata },
      { path: `$.operation_runs[${index}].metadata`, value: record.metadata },
    ]),
    ...parsed.binding_state_events.map((record, index) => ({ path: `$.binding_state_events[${index}].metadata`, value: record.metadata })),
    ...parsed.operation_state_events.map((record, index) => ({ path: `$.operation_state_events[${index}].metadata`, value: record.metadata })),
    ...parsed.lifecycle_events.map((record, index) => ({ path: `$.lifecycle_events[${index}].metadata`, value: record.metadata })),
    ...parsed.decisions.flatMap((record, index) => [
      { path: `$.decisions[${index}].metadata`, value: record.metadata },
      ...record.status_events_through_time.map((status, statusIndex) => ({
        path: `$.decisions[${index}].status_events_through_time[${statusIndex}].metadata`,
        value: status.metadata,
      })),
    ]),
    ...parsed.decision_status_events.map((record, index) => ({ path: `$.decision_status_events[${index}].metadata`, value: record.metadata })),
    ...parsed.journal_entries.map((record, index) => ({ path: `$.journal_entries[${index}].metadata`, value: record.metadata })),
    ...parsed.unknown_records.map((record, index) => ({ path: `$.unknown_records[${index}].payload`, value: record.payload })),
    ...parsed.unknown_relationships.map((record, index) => ({ path: `$.unknown_relationships[${index}].payload`, value: record.payload })),
    ...parsed.semantic_signals.flatMap((record, index) => record.raw_value === null ? [] : [{
      path: `$.semantic_signals[${index}].raw_value`,
      value: record.raw_value,
    }]),
  ]);
  validateCollectionIdentities(parsed, "$");
  validateReferenceIntegrity(parsed, "$");

  for (const unknownRecord of parsed.unknown_records) {
    if (!parsed.semantic_signals.some((signal) => signal.code === "unknown_record_type" && signal.record_id === unknownRecord.id)) {
      fail("$.semantic_signals", `missing unknown_record_type signal for ${unknownRecord.id}`);
    }
  }
  for (const relationship of parsed.unknown_relationships) {
    if (!parsed.semantic_signals.some((signal) => signal.code === "unknown_relationship_type" && signal.record_id === relationship.id)) {
      fail("$.semantic_signals", `missing unknown_relationship_type signal for ${relationship.id}`);
    }
  }
  return parsed;
}

export function parseCanonicalLineageEnvelope(
  value: unknown,
  request?: CanonicalLineageRequest,
): CanonicalLineageEnvelope {
  const item = exactRecord(value, "$", ["schema_version", "request_id", "data", "page", "provenance", "warnings"]);
  literal(item.schema_version, "$.schema_version", ["thread.api.v1"]);
  uuid(item.request_id, "$.request_id");
  const data = parseCanonicalLineage(item.data);
  if (item.page !== null) {
    const page = exactRecord(item.page, "$.page", ["next_cursor", "truncated"]);
    nullableString(page.next_cursor, "$.page.next_cursor");
    boolean(page.truncated, "$.page.truncated");
  }
  const provenance = exactRecord(item.provenance, "$.provenance", ["source", "projection_version", "schema_migration"]);
  literal(provenance.source, "$.provenance.source", ["thread_core"]);
  literal(provenance.projection_version, "$.provenance.projection_version", [CANONICAL_LINEAGE_CONTRACT_VERSION]);
  schemaMigration(provenance.schema_migration, "$.provenance.schema_migration");
  stringArray(item.warnings, "$.warnings", (entry, entryPath) => string(entry, entryPath, true));
  if (request) {
    if (data.context.project_id.toLowerCase() !== request.project_id.toLowerCase()) {
      fail("$.data.context.project_id", "must equal request.project_id");
    }
    if (typeof request.as_of === "string"
      && normalizedInstantMicros(data.context.as_of, "$.data.context.as_of") !== normalizedInstantMicros(request.as_of, "request.as_of")) {
      fail("$.data.context.as_of", "must equal the requested as_of instant");
    }
    if (request.recorded_as_of !== undefined && data.context.recorded_as_of !== request.recorded_as_of) {
      fail("$.data.context.recorded_as_of", "must equal request.recorded_as_of");
    }
    if (request.snapshot_token !== undefined && data.context.snapshot_token !== request.snapshot_token) {
      fail("$.data.context.snapshot_token", "must equal request.snapshot_token");
    }
    if (data.context.projection !== "trace") {
      fail("$.data.context.projection", "must equal the direct lineage trace projection");
    }
    const expectedRoots = request.root_artifact_version_ids
      .map((id) => id.toLowerCase())
      .sort();
    const actualRoots = data.context.root_ids.map((id) => id.toLowerCase());
    const fixedIncompleteRootShape = data.context.temporal_history.status === "incomplete"
      && data.context.truncated
      && actualRoots.length === 0;
    if (!fixedIncompleteRootShape && !sameStrings(actualRoots, expectedRoots)) {
      fail("$.data.context.root_ids", "must equal the requested roots in canonical order");
    }
    const expectedMaxDepth = request.max_depth ?? DEFAULT_LINEAGE_MAX_DEPTH;
    const expectedMaxRecords = request.max_records ?? DEFAULT_LINEAGE_MAX_RECORDS;
    const expectedMaxPaths = request.max_paths ?? DEFAULT_LINEAGE_MAX_PATHS;
    if (data.context.limits.max_depth !== expectedMaxDepth) {
      fail("$.data.context.limits.max_depth", "must equal the requested or default max_depth");
    }
    if (data.context.limits.max_records !== expectedMaxRecords) {
      fail("$.data.context.limits.max_records", "must equal the requested or default max_records");
    }
    if (data.context.limits.max_paths !== expectedMaxPaths) {
      fail("$.data.context.limits.max_paths", "must equal the requested or default max_paths");
    }
  }
  return value as CanonicalLineageEnvelope;
}

function validateCanonicalLineageRequest(request: CanonicalLineageRequest): void {
  const item = exactRecord(
    request,
    "request",
    ["project_id", "root_artifact_version_ids"],
    ["direction", "max_depth", "max_records", "max_paths", "as_of", "recorded_as_of", "snapshot_token"],
  );
  requestUuid(item.project_id, "request.project_id");
  const roots = stringArray(
    item.root_artifact_version_ids,
    "request.root_artifact_version_ids",
    requestUuid,
  );
  if (roots.length < 1 || roots.length > 100) fail("request.root_artifact_version_ids", "expected between 1 and 100 roots");
  const normalizedRoots = roots.map((root) => root.toLowerCase());
  if (new Set(normalizedRoots).size !== roots.length) fail("request.root_artifact_version_ids", "root IDs must be unique");
  if (!sameStrings(normalizedRoots, [...normalizedRoots].sort())) {
    fail("request.root_artifact_version_ids", "root IDs must use canonical order");
  }
  if (item.direction !== undefined) literal(item.direction, "request.direction", ["upstream", "downstream", "both"]);
  if (item.max_depth !== undefined) integer(item.max_depth, "request.max_depth", 1, 32);
  const maxRecords = item.max_records === undefined ? 200 : integer(item.max_records, "request.max_records", 1, 500);
  if (roots.length > maxRecords) fail("request.max_records", "must cover every requested root");
  if (item.max_paths !== undefined) integer(item.max_paths, "request.max_paths", 1, 5000);
  if (item.as_of !== undefined && item.as_of !== null) awareTimestamp(item.as_of, "request.as_of");
  validateTemporalReplayFields(item, "request");
}

export async function fetchCanonicalLineage(request: CanonicalLineageRequest): Promise<CanonicalLineageEnvelope> {
  validateCanonicalLineageRequest(request);
  const response = await apiClient<unknown>(CANONICAL_LINEAGE_PATH, {
    method: "POST",
    body: request,
  });
  return parseCanonicalLineageEnvelope(response, request);
}

const QUERY_PROJECTIONS = ["snapshot", "search", "focus", "neighbourhood", "trace", "hierarchy", "summary"] as const;
const QUERY_GROUPS = ["artifact_kind", "lifecycle_state", "model_classification"] as const;

function validateArtifactVersionRoot(
  value: unknown,
  path: string,
  requestDocument = false,
): void {
  const item = exactRecord(value, path, ["entity_kind", "id"]);
  literal(item.entity_kind, `${path}.entity_kind`, ["artifact_version"]);
  (requestDocument ? requestUuid : uuid)(item.id, `${path}.id`);
}

function validateQueryFilters(value: unknown, path: string): void {
  const item = exactRecord(
    value,
    path,
    [],
    ["artifact_kinds", "lifecycle_states", "model_classifications", "name_contains", "created_after", "created_before"],
  );
  const artifactKinds = item.artifact_kinds === undefined
    ? []
    : stringArray(item.artifact_kinds, `${path}.artifact_kinds`);
  const lifecycleStates = item.lifecycle_states === undefined
    ? []
    : stringArray(item.lifecycle_states, `${path}.lifecycle_states`);
  const modelClassifications = item.model_classifications === undefined
    ? []
    : array(item.model_classifications, `${path}.model_classifications`).map((entry, index) => (
      literal(
        entry,
        `${path}.model_classifications[${index}]`,
        ["generic", "instance", "unclassified"] as const,
      )
    ));
  if (artifactKinds.length > 50) fail(`${path}.artifact_kinds`, "must contain at most 50 values");
  if (lifecycleStates.length > 20) fail(`${path}.lifecycle_states`, "must contain at most 20 values");
  if (new Set(modelClassifications).size !== modelClassifications.length) {
    fail(`${path}.model_classifications`, "values must be unique");
  }
  const canonicalModelOrder: CanonicalModelInstanceClassification[] = ["generic", "instance", "unclassified"];
  if (!sameStrings(modelClassifications, [...modelClassifications].sort(
    (left, right) => canonicalModelOrder.indexOf(left) - canonicalModelOrder.indexOf(right),
  ))) {
    fail(`${path}.model_classifications`, "values must use canonical generic, instance, unclassified order");
  }
  const trimmedArtifactKinds = artifactKinds.map((entry) => entry.trim());
  const trimmedLifecycleStates = lifecycleStates.map((entry) => entry.trim());
  if (trimmedArtifactKinds.some((entry) => !entry)) fail(`${path}.artifact_kinds`, "values must not be blank");
  if (trimmedLifecycleStates.some((entry) => !entry)) fail(`${path}.lifecycle_states`, "values must not be blank");
  if (new Set(trimmedArtifactKinds).size !== trimmedArtifactKinds.length) fail(`${path}.artifact_kinds`, "values must be unique after trimming");
  if (new Set(trimmedLifecycleStates).size !== trimmedLifecycleStates.length) fail(`${path}.lifecycle_states`, "values must be unique after trimming");
  if (item.name_contains !== undefined) {
    const nameContains = string(item.name_contains, `${path}.name_contains`);
    if (!nameContains.trim()) fail(`${path}.name_contains`, "must not be blank");
    if (nameContains.length > 300) fail(`${path}.name_contains`, "must contain at most 300 characters");
  }
  const createdAfter = item.created_after === undefined ? null : awareTimestamp(item.created_after, `${path}.created_after`);
  const createdBefore = item.created_before === undefined ? null : awareTimestamp(item.created_before, `${path}.created_before`);
  if (createdAfter && createdBefore && compareAwareTimestamps(createdAfter, createdBefore) > 0) {
    fail(path, "created_after must not be later than created_before");
  }
}

function queryFiltersHaveValue(value: unknown): boolean {
  if (value === undefined) return false;
  const item = value as UnknownRecord;
  return (Array.isArray(item.artifact_kinds) && item.artifact_kinds.length > 0)
    || (Array.isArray(item.lifecycle_states) && item.lifecycle_states.length > 0)
    || (Array.isArray(item.model_classifications) && item.model_classifications.length > 0)
    || (typeof item.name_contains === "string" && item.name_contains.length > 0)
    || typeof item.created_after === "string"
    || typeof item.created_before === "string";
}

function validateCanonicalLineageQueryRequest(request: CanonicalLineageQueryRequest): void {
  const item = exactRecord(
    request,
    "request",
    ["project_id", "projection"],
    ["root_refs", "direction", "max_depth", "max_records", "max_paths", "page_size", "cursor", "as_of", "recorded_as_of", "snapshot_token", "query", "filters", "group_by"],
  );
  requestUuid(item.project_id, "request.project_id");
  const projection = literal(item.projection, "request.projection", QUERY_PROJECTIONS);
  const roots = item.root_refs === undefined ? [] : array(item.root_refs, "request.root_refs");
  roots.forEach((root, index) => (
    validateArtifactVersionRoot(root, `request.root_refs[${index}]`, true)
  ));
  const rootIds = roots.map((root) => ((root as UnknownRecord).id as string).toLowerCase());
  if (new Set(rootIds).size !== rootIds.length) fail("request.root_refs", "root identities must be unique");
  if (projection === "focus" && roots.length !== 1) fail("request.root_refs", "focus requires exactly one root");
  if ((projection === "neighbourhood" || projection === "trace") && (roots.length < 1 || roots.length > 100)) {
    fail("request.root_refs", `${projection} requires between 1 and 100 roots`);
  }
  if (["snapshot", "search", "hierarchy", "summary"].includes(projection) && roots.length > 0) {
    fail("request.root_refs", `${projection} does not accept roots`);
  }
  if (item.direction !== undefined) literal(item.direction, "request.direction", ["upstream", "downstream", "both"]);
  if (item.max_depth !== undefined) integer(item.max_depth, "request.max_depth", 1, 32);
  if (item.max_records !== undefined) integer(item.max_records, "request.max_records", 1, 500);
  if (item.max_paths !== undefined) integer(item.max_paths, "request.max_paths", 1, 5000);
  if (item.page_size !== undefined) integer(item.page_size, "request.page_size", 1, 100);
  if (item.cursor !== undefined && string(item.cursor, "request.cursor").length > 2048) {
    fail("request.cursor", "must contain at most 2048 characters");
  }
  if (item.as_of !== undefined && item.as_of !== null) awareTimestamp(item.as_of, "request.as_of");
  validateTemporalReplayFields(item, "request");
  if (item.query !== undefined) {
    const query = string(item.query, "request.query");
    if (!query.trim()) fail("request.query", "must not be blank");
    if (query.length > 300) fail("request.query", "must contain at most 300 characters");
  }
  if (projection === "search" && item.query === undefined && !queryFiltersHaveValue(item.filters)) {
    fail("request.query", "search requires a query or filters");
  }
  if (item.filters !== undefined) validateQueryFilters(item.filters, "request.filters");
  if (item.group_by !== undefined) {
    literal(item.group_by, "request.group_by", QUERY_GROUPS);
    if (projection !== "hierarchy" && projection !== "summary") {
      fail("request.group_by", "is only valid for hierarchy or summary projections");
    }
  }
  if (projection === "hierarchy" && item.group_by === undefined) {
    fail("request.group_by", "hierarchy requires a grouping dimension");
  }
  const maxRecords = item.max_records === undefined ? 200 : item.max_records as number;
  const pageSize = item.page_size === undefined ? 50 : item.page_size as number;
  if ((projection === "snapshot" || projection === "search") && pageSize * 4 > maxRecords) {
    fail("request.page_size", "snapshot/search reserve four canonical records per page root");
  }
  if ((projection === "focus" || projection === "neighbourhood" || projection === "trace") && pageSize * 2 > maxRecords) {
    fail("request.page_size", "rooted projections reserve two canonical records per page candidate");
  }
}

function canonicalRecordCount(model: CanonicalLineageReadModel): number {
  return model.artifacts.length
    + model.artifact_versions.length
    + model.operation_specs.length
    + model.tool_snapshots.length
    + model.actors.length
    + model.operation_runs.length
    + model.bindings.length
    + model.binding_state_events.length
    + model.operation_state_events.length
    + model.supersessions.length
    + model.merge_inputs.length
    + model.topology_retirements.length
    + model.lifecycle_events.length
    + model.decisions.length
    + model.decision_evidence.length
    + model.decision_status_events.length
    + model.journal_entries.length
    + model.unknown_records.length
    + model.unknown_relationships.length;
}

function parseCanonicalLineageProjectionResult(value: unknown, path: string): CanonicalLineageProjectionResult {
  const item = exactRecord(
    value,
    path,
    ["contract_version", "projection", "effective_as_of", "recorded_as_of", "snapshot_token", "roots", "lineage", "buckets", "next_cursor", "truncated", "warnings"],
  );
  literal(item.contract_version, `${path}.contract_version`, [CANONICAL_LINEAGE_PROJECTION_CONTRACT_VERSION]);
  const projection = literal(item.projection, `${path}.projection`, QUERY_PROJECTIONS);
  const effectiveAsOf = fixedUtcTimestamp(item.effective_as_of, `${path}.effective_as_of`);
  const recordedAsOf = fixedUtcTimestamp(item.recorded_as_of, `${path}.recorded_as_of`);
  const token = snapshotToken(item.snapshot_token, `${path}.snapshot_token`);
  if (compareInstantsAtMicrosecondPrecision(effectiveAsOf, recordedAsOf) > 0) {
    fail(`${path}.effective_as_of`, "must not be later than recorded_as_of");
  }
  const roots = array(item.roots, `${path}.roots`);
  if (roots.length > 100) fail(`${path}.roots`, "must contain at most 100 items");
  roots.forEach((root, index) => validateArtifactVersionRoot(root, `${path}.roots[${index}]`));
  const lineage = parseCanonicalLineage(item.lineage);
  if (lineage.context.projection !== projection) {
    fail(`${path}.lineage.context.projection`, "must match the outer projection");
  }
  if (lineage.context.as_of !== effectiveAsOf) {
    fail(`${path}.lineage.context.as_of`, "must match effective_as_of");
  }
  if (lineage.context.recorded_as_of !== recordedAsOf) {
    fail(`${path}.lineage.context.recorded_as_of`, "must match recorded_as_of");
  }
  if (lineage.context.snapshot_token !== token) {
    fail(`${path}.lineage.context.snapshot_token`, "must match snapshot_token");
  }
  const rootIds = roots.map((root) => (root as CanonicalLineageArtifactVersionRoot).id);
  if (new Set(rootIds).size !== rootIds.length) fail(`${path}.roots`, "root identities must be unique");
  if (lineage.context.root_ids.length > 100) {
    fail(`${path}.lineage.context.root_ids`, "one projection page may hydrate at most 100 roots");
  }
  if (projection === "snapshot" || projection === "search") {
    if (rootIds.length !== lineage.context.root_ids.length || rootIds.some((id, index) => lineage.context.root_ids[index] !== id)) {
      fail(`${path}.lineage.context.root_ids`, "must match the hydrated outer roots in order");
    }
  } else if (projection === "hierarchy" || projection === "summary") {
    if (roots.length > 0 || lineage.context.root_ids.length > 0) {
      fail(`${path}.roots`, `${projection} projections must not expose or hydrate roots`);
    }
  } else {
    const fixedIncompleteRootShape = lineage.context.temporal_history.status === "incomplete"
      && lineage.context.truncated
      && roots.length === 0
      && lineage.context.root_ids.length === 0
      && canonicalRecordCount(lineage) === 0;
    if (!fixedIncompleteRootShape && projection === "focus" && roots.length !== 1) {
      fail(`${path}.roots`, "focus must retain exactly one caller seed root");
    }
    if (!fixedIncompleteRootShape
      && (projection === "neighbourhood" || projection === "trace")
      && (roots.length < 1 || roots.length > 100)) {
      fail(`${path}.roots`, `${projection} must retain between 1 and 100 caller seed roots`);
    }
    if (!fixedIncompleteRootShape && lineage.context.root_ids.length < 1) {
      fail(`${path}.lineage.context.root_ids`, `${projection} must hydrate at least one page root`);
    }
  }
  const buckets = array(item.buckets, `${path}.buckets`);
  if (buckets.length > 100) fail(`${path}.buckets`, "must contain at most 100 buckets");
  buckets.forEach((value, index) => {
    const bucketPath = `${path}.buckets[${index}]`;
    const bucket = exactRecord(value, bucketPath, ["dimension", "key", "label", "count"], ["representative_root"]);
    literal(bucket.dimension, `${bucketPath}.dimension`, QUERY_GROUPS);
    const key = string(bucket.key, `${bucketPath}.key`);
    const label = string(bucket.label, `${bucketPath}.label`);
    if (key.length > 300) fail(`${bucketPath}.key`, "must contain at most 300 characters");
    if (label.length > 300) fail(`${bucketPath}.label`, "must contain at most 300 characters");
    integer(bucket.count, `${bucketPath}.count`, 1);
    if (bucket.representative_root !== undefined && bucket.representative_root !== null) {
      validateArtifactVersionRoot(bucket.representative_root, `${bucketPath}.representative_root`);
    }
  });
  if (buckets.some((bucket) => (bucket as CanonicalLineageProjectionBucket).dimension !== (buckets[0] as CanonicalLineageProjectionBucket | undefined)?.dimension)) {
    fail(`${path}.buckets`, "all buckets in one projection must use the same dimension");
  }
  if ((projection === "hierarchy" || projection === "summary") && canonicalRecordCount(lineage) !== 0) {
    fail(`${path}.lineage`, `${projection} projections must use buckets rather than normalized records`);
  }
  if (projection !== "hierarchy" && projection !== "summary" && buckets.length > 0) {
    fail(`${path}.buckets`, `${projection} projections do not accept aggregate buckets`);
  }
  if (item.next_cursor !== null && string(item.next_cursor, `${path}.next_cursor`).length > 2048) {
    fail(`${path}.next_cursor`, "must contain at most 2048 characters");
  }
  const truncated = boolean(item.truncated, `${path}.truncated`);
  if (item.next_cursor !== null && !truncated) {
    fail(`${path}.truncated`, "must be true while another cursor page is available");
  }
  if (lineage.context.truncated && !truncated) {
    fail(`${path}.truncated`, "must be true when lineage.context.truncated is true");
  }
  validateLineageWarnings(item.warnings, `${path}.warnings`);
  return value as CanonicalLineageProjectionResult;
}

export function parseCanonicalLineageProjectionEnvelope(
  value: unknown,
  request?: CanonicalLineageQueryRequest,
): CanonicalLineageProjectionEnvelope {
  const item = exactRecord(value, "$", ["schema_version", "request_id", "data", "page", "provenance", "warnings"]);
  literal(item.schema_version, "$.schema_version", ["thread.api.v1"]);
  uuid(item.request_id, "$.request_id");
  const data = parseCanonicalLineageProjectionResult(item.data, "$.data");
  const page = exactRecord(item.page, "$.page", ["next_cursor", "truncated"]);
  const pageCursor = nullableString(page.next_cursor, "$.page.next_cursor");
  const pageTruncated = boolean(page.truncated, "$.page.truncated");
  if (data.next_cursor !== pageCursor || data.truncated !== pageTruncated) {
    fail("$.page", "must mirror data.next_cursor and data.truncated");
  }
  const provenance = exactRecord(item.provenance, "$.provenance", ["source", "projection_version", "schema_migration"]);
  literal(provenance.source, "$.provenance.source", ["thread_core"]);
  literal(provenance.projection_version, "$.provenance.projection_version", [CANONICAL_LINEAGE_PROJECTION_CONTRACT_VERSION]);
  schemaMigration(provenance.schema_migration, "$.provenance.schema_migration");
  stringArray(item.warnings, "$.warnings", (entry, entryPath) => string(entry, entryPath, true));
  if (request) {
    if (data.lineage.context.project_id.toLowerCase() !== request.project_id.toLowerCase()) {
      fail("$.data.lineage.context.project_id", "must equal request.project_id");
    }
    if (typeof request.as_of === "string"
      && normalizedInstantMicros(data.effective_as_of, "$.data.effective_as_of") !== normalizedInstantMicros(request.as_of, "request.as_of")) {
      fail("$.data.effective_as_of", "must equal the requested as_of instant");
    }
    if (request.recorded_as_of !== undefined && data.recorded_as_of !== request.recorded_as_of) {
      fail("$.data.recorded_as_of", "must equal request.recorded_as_of");
    }
    if (request.snapshot_token !== undefined && data.snapshot_token !== request.snapshot_token) {
      fail("$.data.snapshot_token", "must equal request.snapshot_token");
    }
    if (data.projection !== request.projection) {
      fail("$.data.projection", "must equal request.projection");
    }
    const rootedProjection = request.projection === "focus"
      || request.projection === "neighbourhood"
      || request.projection === "trace";
    if (rootedProjection) {
      const expectedRoots = (request.root_refs ?? [])
        .map((root) => root.id.toLowerCase())
        .sort();
      const actualRoots = data.roots.map((root) => root.id.toLowerCase());
      const fixedIncompleteRootShape = data.lineage.context.temporal_history.status === "incomplete"
        && data.lineage.context.truncated
        && actualRoots.length === 0
        && data.lineage.context.root_ids.length === 0;
      if (!fixedIncompleteRootShape && !sameStrings(actualRoots, expectedRoots)) {
        fail("$.data.roots", "must equal the requested rooted caller seeds in canonical order");
      }
    }
    const expectedMaxRecords = request.max_records ?? DEFAULT_LINEAGE_MAX_RECORDS;
    const expectedMaxPaths = request.max_paths ?? DEFAULT_LINEAGE_MAX_PATHS;
    if (data.lineage.context.limits.max_records !== expectedMaxRecords) {
      fail("$.data.lineage.context.limits.max_records", "must equal the requested or default max_records");
    }
    if (data.lineage.context.limits.max_paths !== expectedMaxPaths) {
      fail("$.data.lineage.context.limits.max_paths", "must equal the requested or default max_paths");
    }
    const discoveryProjection = request.projection === "snapshot" || request.projection === "search";
    const expectedMaxDepth = discoveryProjection
      ? 1
      : request.max_depth ?? DEFAULT_LINEAGE_MAX_DEPTH;
    if (data.lineage.context.limits.max_depth !== expectedMaxDepth) {
      fail("$.data.lineage.context.limits.max_depth", "must equal the applied request projection max_depth");
    }
  }
  return value as CanonicalLineageProjectionEnvelope;
}

export async function fetchCanonicalLineageProjection(
  request: CanonicalLineageQueryRequest,
  options: { signal?: AbortSignal } = {},
): Promise<CanonicalLineageProjectionEnvelope> {
  validateCanonicalLineageQueryRequest(request);
  const response = await apiClient<unknown>(CANONICAL_LINEAGE_PROJECTION_PATH, {
    method: "POST",
    body: request,
    signal: options.signal,
  });
  return parseCanonicalLineageProjectionEnvelope(response, request);
}
